import { AuthCodeDeliveryDetails } from '@aws-amplify/auth/dist/esm/types/models'
import {
  confirmSignIn,
  fetchAuthSession,
  rememberDevice,
  signIn,
  signOut,
} from 'aws-amplify/auth'
import axios from 'axios'
import { CallHistoryMethodAction, push } from 'connected-react-router'
import { Location } from 'history'
import moment from 'moment-timezone'
import React from 'react'
import { Action } from 'redux'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { ExtendAnalyticsBrowser } from 'src/analytics/AnalyticsContext'
import { selectFilteredOrganizationInviteModels } from 'src/app/OrganizationInvites/redux/organizationInvites.selectors'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import {
  PostResetPasswordToastMessage,
  VerifiedPhoneNumberToastMessage,
  VerifiedUpdatedPhoneNumberToastMessage,
} from 'src/app/Toast/Toast'
import {
  getUserPoolClientIdCookieName,
  getUserPoolIdCookieName,
} from 'src/context/UserProvider/UserProvider'
import { PerSessionValuesType } from 'src/cookies'
import {
  AuthTypes,
  ExtendLocales,
  MfaDeliveryMedium,
  MfaPreferences,
  UserStatus,
} from 'src/enums'
import Trns, { translateSingleString } from 'src/localization/Trns'
import { IssuerListResponse } from 'src/models/issuer.models'
import { OrganizationInviteModel } from 'src/models/organizations.models'
import { OrgInviteStatus } from 'src/models/people.models'
import { EmulatedUserSessionTokenListResponse } from 'src/models/token.models'
import { UserListModel, UserListResponse } from 'src/models/user.models'
import { connectedPush, connectedReplace } from 'src/navigation/Lnk'
import { homeLocation } from 'src/navigation/locations'
import {
  cspSigninUrl,
  homeUrl,
  onboardingUrl,
  resetPasswordUrl,
  signinUrl,
  verifyEmailUrl,
} from 'src/navigation/urls'
import {
  createSetIsExtraVerificationPopupOpen,
  createSetIsSessionExpiringPopupOpen,
  createSetIsUpdatePhoneNumberPopupOpen,
  createSetIsVerifyPhoneNumberPopupOpen,
  SetIsSessionExpiringPopupOpenAction,
} from 'src/redux/actions/app.actions'
import {
  getOrganizationInvites,
  putOrganizationInvite,
} from 'src/redux/actions/data/organizations.actions'
import { LocationState, RootState } from 'src/redux/createRootReducer'
import {
  selectAuthedRedirectLocationFromState,
  selectAuthTypeFromState,
  selectCspRefreshTokenFromState,
  selectIsAuthenticatedFromState,
  selectNullSafeLocaleFromState,
  selectRefreshTokenFromState,
  selectUserSessionTokenIdFromState,
} from 'src/redux/selectors/auth.selectors'
import {
  selectAuthedJsonApiRequestConfig,
  selectCspAuthedIssuerIdJsonApiRequestConfig,
  selectCspAuthedJsonApiRequestConfig,
  selectUnauthedJsonApiRequestConfig,
} from 'src/redux/selectors/axios.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectLocationFromState } from 'src/redux/selectors/router.selectors'
import {
  selectBrandId,
  selectEnvironmentFromState,
} from 'src/redux/selectors/session.selectors'
import { AuthTypeRequest } from 'src/requests/authType.request'
import { CognitoResetPasswordConfirmRequest } from 'src/requests/cognitoResetPasswordConfirm.request'
import { CognitoResetPasswordInitRequest } from 'src/requests/cognitoResetPasswordInit.request'
import { PostUserRequest } from 'src/requests/postUser.request'
import { configureAmplify } from 'src/utils/cognito.utils'
import { BackendErrorPayload } from 'src/utils/error.utils'
import { isSSOLoginEnabled } from 'src/utils/featureFlags'
import { normalizeEmail } from 'src/utils/string.utils'
import Cookies from 'universal-cookie'

export const RESET_VERIFICATION_ERRORS = 'auth/RESET_VERIFICATION_ERRORS'

export interface ResetVerificationErrorsAction {
  type: typeof RESET_VERIFICATION_ERRORS
}

export const resetVerificationErrors = (): ResetVerificationErrorsAction => ({
  type: RESET_VERIFICATION_ERRORS,
})

export const SET_USER_STATUS = 'auth/SET_USER_STATUS'
export interface SetUserStatusAction {
  type: typeof SET_USER_STATUS
  payload: {
    status?: UserStatus
  }
}
export const createSetUserStatus = (
  status?: UserStatus,
): SetUserStatusAction => ({
  type: SET_USER_STATUS,
  payload: {
    status,
  },
})

export const SET_EMAIL = 'auth/SET_EMAIL'
export interface SetEmailAction {
  type: typeof SET_EMAIL
  payload: {
    email: string
  }
}
export const createSetEmail = (email: string): SetEmailAction => ({
  type: SET_EMAIL,
  payload: {
    email: normalizeEmail(email),
  },
})

export const SET_PER_SESSION_VALUES = 'auth/SET_PER_SESSION_VALUES'
export interface SetPerSessionValues {
  type: typeof SET_PER_SESSION_VALUES
  payload: {
    perSessionValues: Partial<PerSessionValuesType>
  }
}
export const createSetPerSessionValues = (
  newValues: Partial<PerSessionValuesType>,
): SetPerSessionValues => ({
  type: SET_PER_SESSION_VALUES,
  payload: {
    perSessionValues: newValues,
  },
})

export const GET_ME_INIT = 'auth/GET_ME_INIT'
export const GET_ME_SUCCESS = 'auth/GET_ME_SUCCESS'
export const GET_ME_FAILURE = 'auth/GET_ME_FAILURE'

export interface GetMeAction {
  type: typeof GET_ME_INIT
}
export interface GetMeSuccessAction {
  type: typeof GET_ME_SUCCESS
  payload: UserListResponse
  meta?: { source?: 'react-query' | 'redux' }
}
export interface GetMeFailureAction {
  type: typeof GET_ME_FAILURE
}

export type GetMeActions = GetMeAction | GetMeSuccessAction | GetMeFailureAction

const createGetMeAction = (): GetMeAction => ({
  type: GET_ME_INIT,
})
const createGetMeSuccessAction = (
  payload: UserListResponse,
): GetMeSuccessAction => ({
  type: GET_ME_SUCCESS,
  payload: payload,
})
const createGetMeFailureAction = (): GetMeFailureAction => ({
  type: GET_ME_FAILURE,
})

export const getMe = (): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetMeActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetMeActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetMeAction())
    const state = getState()
    return axios.get('/users/me', selectAuthedJsonApiRequestConfig(state)).then(
      (response) => {
        dispatch(createGetMeSuccessAction(response.data))
      },
      () => {
        dispatch(createGetMeFailureAction())
      },
    )
  }
}

export const GET_CSP_ME_INIT = 'auth/GET_CSP_ME_INIT'
export const GET_CSP_ME_SUCCESS = 'auth/GET_CSP_ME_SUCCESS'
export const GET_CSP_ME_FAILURE = 'auth/GET_CSP_ME_FAILURE'

interface GetCspMeSuccessPayload {
  user: UserListResponse
  issuers: IssuerListResponse[]
}

export interface GetCspMeAction {
  type: typeof GET_CSP_ME_INIT
}
export interface GetCspMeSuccessAction {
  type: typeof GET_CSP_ME_SUCCESS
  payload: GetCspMeSuccessPayload
}
export interface GetCspMeFailureAction {
  type: typeof GET_CSP_ME_FAILURE
}

export type GetCspMeActions =
  | GetCspMeAction
  | GetCspMeSuccessAction
  | GetCspMeFailureAction

const createGetCspMeAction = (): GetCspMeAction => ({
  type: GET_CSP_ME_INIT,
})
const createGetCspMeSuccessAction = (
  payload: GetCspMeSuccessPayload,
): GetCspMeSuccessAction => ({
  type: GET_CSP_ME_SUCCESS,
  payload: payload,
})
const createGetCspMeFailureAction = (): GetCspMeFailureAction => ({
  type: GET_CSP_ME_FAILURE,
})

export const getCspMe = (): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetCspMeActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetCspMeActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetCspMeAction())
    const state = getState()
    return axios
      .get('/csp/me', selectCspAuthedJsonApiRequestConfig(state))
      .then(
        (response) => {
          dispatch(createGetCspMeSuccessAction(response.data))
        },
        () => {
          dispatch(createGetCspMeFailureAction())
        },
      )
  }
}

export const GET_HEARTBEAT_INIT = 'auth/GET_HEARTBEAT_INIT'
export const GET_HEARTBEAT_SUCCESS = 'auth/GET_HEARTBEAT_SUCCESS'
export const GET_HEARTBEAT_FAILURE = 'auth/GET_HEARTBEAT_FAILURE'

export interface GetHeartbeatInitAction {
  type: typeof GET_HEARTBEAT_INIT
}
export interface GetHeartbeatSuccessAction {
  type: typeof GET_HEARTBEAT_SUCCESS
  payload: {
    authedRedirectLocation: Location
  }
}
export interface GetHeartbeatFailureAction {
  type: typeof GET_HEARTBEAT_FAILURE
  payload: {
    isCsp?: boolean
    authedRedirectLocation: Location
  }
}

export type GetHeartbeatActions =
  | GetHeartbeatInitAction
  | GetHeartbeatSuccessAction
  | GetHeartbeatFailureAction

const createGetHeartbeatInitAction = (): GetHeartbeatInitAction => ({
  type: GET_HEARTBEAT_INIT,
})
const createGetHeartbeatSuccessAction = (payload: {
  authedRedirectLocation: Location<LocationState>
}): GetHeartbeatSuccessAction => ({
  type: GET_HEARTBEAT_SUCCESS,
  payload,
})
const createGetHeartbeatFailureAction = (payload: {
  authedRedirectLocation: Location<LocationState>
}): GetHeartbeatFailureAction => ({
  type: GET_HEARTBEAT_FAILURE,
  payload,
})

export const getHeartbeat = (
  isCsp?: boolean,
): ThunkAction<Promise<void>, RootState, void, GetHeartbeatActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetHeartbeatActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetHeartbeatInitAction())
    const state = getState()
    return axios
      .get(
        '/heartbeat',
        isCsp
          ? selectCspAuthedJsonApiRequestConfig(state)
          : selectAuthedJsonApiRequestConfig(state),
      )
      .then(
        () => {
          dispatch(
            createGetHeartbeatSuccessAction({
              authedRedirectLocation: { ...location, state: {} },
            }),
          )
        },
        (err) => {
          const location = selectLocationFromState(getState())
          dispatch(
            createGetHeartbeatFailureAction({
              ...err.response,
              isCsp: isCsp,
              authedRedirectLocation: location,
            }),
          )
        },
      )
  }
}

export const POST_LOOKUP_INIT = 'auth/POST_LOOKUP_INIT'
export const POST_LOOKUP_SUCCESS = 'auth/POST_LOOKUP_SUCCESS'
export const POST_LOOKUP_FAILURE = 'auth/POST_LOOKUP_FAILURE'
export const POST_COGNITO_LOOKUP_SUCCESS = 'auth/POST_COGNITO_LOOKUP_SUCCESS'
export const POST_COGNITO_LOOKUP_FAILURE = 'auth/POST_COGNITO_LOOKUP_FAILURE'

export type PostLookupFailurePayload = BackendErrorPayload<string>

export interface PostLookupInitAction {
  type: typeof POST_LOOKUP_INIT
}
export interface PostLookupSuccessAction {
  type: typeof POST_LOOKUP_SUCCESS
}
export interface PostLookupFailureAction {
  type: typeof POST_LOOKUP_FAILURE
  payload: PostLookupFailurePayload
}
export interface PostCognitoLookupSuccessAction {
  type: typeof POST_COGNITO_LOOKUP_SUCCESS
}
export interface PostCognitoLookupFailureAction {
  type: typeof POST_COGNITO_LOOKUP_FAILURE
  payload: string
}

export type PostLookupActions =
  | PostLookupInitAction
  | PostLookupSuccessAction
  | PostLookupFailureAction
  | PostCognitoLookupSuccessAction
  | PostCognitoLookupFailureAction

// TODO: refactor these action names to not reference lookup
const createPostLookupInitAction = (): PostLookupInitAction => ({
  type: POST_LOOKUP_INIT,
})

const createPostLookupFailureAction = (
  payload: PostLookupFailurePayload,
): PostLookupFailureAction => ({
  type: POST_LOOKUP_FAILURE,
  payload,
})

const createPostCognitoLookupFailureAction = (
  payload: string,
): PostCognitoLookupFailureAction => ({
  type: POST_COGNITO_LOOKUP_FAILURE,
  payload,
})

let startTimeStamp: moment.Moment

export function getStartTimeStamp(): moment.Moment {
  return startTimeStamp
}

export const extendSignIn = (
  email: string,
  password?: string,
  newCode?: boolean,
  analytics?: ExtendAnalyticsBrowser,
): ThunkAction<Promise<void>, RootState, void, PostLookupActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostLookupActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostLookupInitAction())
    let authType = selectAuthTypeFromState(getState())

    const handleSuccessfulLoginAnalytics = (): void => {
      analytics?.track('successful sign in', {
        screen: signinUrl(),
      })
    }

    // just in case authType is undefined at this step
    if (authType === undefined) {
      await dispatch(postAuthType({ data: email }))
      authType = selectAuthTypeFromState(getState())
    }

    if (authType === AuthTypes.auth0) {
      const action = await dispatch(postSigninThenPush(email, password))
      if (action.type === POST_SIGNIN_SUCCESS) {
        handleSuccessfulLoginAnalytics()
      } else {
        dispatch(createPostLookupFailureAction(action.payload))
      }
    } else if (authType === AuthTypes.cognito) {
      const action = await dispatch(
        postAmplifySignin(email, password, !!newCode),
      )
      if (action !== undefined) {
        if (action.type === POST_AMPLIFY_SIGNIN_SUCCESS) {
          handleSuccessfulLoginAnalytics()
        } else {
          dispatch(createPostCognitoLookupFailureAction(action.payload))
        }
      }
    }
  }
}

export const postAuthCspLogin = (
  email: string,
  password?: string,
  newCode?: boolean,
): ThunkAction<Promise<void>, RootState, void, PostLookupActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostLookupActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostLookupInitAction())
    let authType = selectAuthTypeFromState(getState())

    // just in case authType is undefined at this step
    if (authType === undefined) {
      await dispatch(postAuthType({ data: email }))
      authType = selectAuthTypeFromState(getState())
    }

    if (authType === AuthTypes.auth0) {
      const action = await dispatch(postCspLogin(email, password))
      if (action.type === POST_CSP_LOGIN_FAILURE) {
        dispatch(createPostLookupFailureAction(action.payload))
      }
    } else if (authType === AuthTypes.cognito) {
      const action = await dispatch(
        postAmplifySignin(email, password, !!newCode, true),
      )
      if (action !== undefined) {
        if (action.type === POST_AMPLIFY_SIGNIN_FAILURE) {
          dispatch(createPostCognitoLookupFailureAction(action.payload))
        }
      }
    }
  }
}

export const POST_SIGNIN_INIT = 'auth/POST_SIGNIN_INIT'
export const POST_SIGNIN_SUCCESS = 'auth/POST_SIGNIN_SUCCESS'
export const POST_SIGNIN_FAILURE = 'auth/POST_SIGNIN_FAILURE'

interface PostSigninSuccessPayload {
  refreshToken: string
  token: string
  user: UserListResponse
  acceptOrgInvite?: boolean
}

export interface PostSigninInitAction {
  type: typeof POST_SIGNIN_INIT
}
export interface PostSigninSuccessAction {
  type: typeof POST_SIGNIN_SUCCESS
  payload: PostSigninSuccessPayload
}
export interface PostSigninFailureAction {
  type: typeof POST_SIGNIN_FAILURE
  payload: BackendErrorPayload<string>
}

export type PostSigninActions =
  | PostSigninInitAction
  | PostSigninSuccessAction
  | PostSigninFailureAction

const createPostSigninInitAction = (): PostSigninInitAction => ({
  type: POST_SIGNIN_INIT,
})
const createPostSigninSuccessAction = (
  payload: PostSigninSuccessPayload,
): PostSigninSuccessAction => ({
  type: POST_SIGNIN_SUCCESS,
  payload,
})
const createPostSigninFailureAction = (
  payload: BackendErrorPayload<string>,
): PostSigninFailureAction => ({
  type: POST_SIGNIN_FAILURE,
  payload: payload,
})

export const postSigninThenPush = (
  email: string,
  password?: string,
  acceptOrgInvite?: boolean,
): ThunkAction<
  Promise<PostSigninSuccessAction | PostSigninFailureAction>,
  RootState,
  void,
  PostSigninActions | CallHistoryMethodAction
> => {
  // AUTH0 SIGN IN?
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      | PostSigninActions
      | CallHistoryMethodAction
      | UpdateAcceptOrgInviteAction
      | UpdateOrgInviteErrorAction
    >,
    getState: () => RootState,
  ): Promise<PostSigninSuccessAction | PostSigninFailureAction> => {
    dispatch(createPostSigninInitAction())
    return axios
      .post(
        `/signin`,
        { email, password },
        selectUnauthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          const authedRedirectLocation =
            selectAuthedRedirectLocationFromState(getState())
          const postSigninSuccessActionDispatched = dispatch(
            createPostSigninSuccessAction({
              ...response.data,
              acceptOrgInvite: !!acceptOrgInvite,
            }),
          )
          // do this after posting sign in success so that the user exists in the store
          // check auth state flow to see if we are coming from email flow
          if (acceptOrgInvite) {
            // fetch org invites and accept the first one if it exists
            await dispatch(getOrganizationInvites())
            const organizationInvites: unknown[] =
              selectFilteredOrganizationInviteModels(getState())
            if (organizationInvites?.length > 0) {
              const firstOrgInvite = Object.values(
                organizationInvites,
              )[0] as OrganizationInviteModel
              await dispatch(
                putOrganizationInvite(OrgInviteStatus.Accepted, firstOrgInvite),
              )
              // update the user in the store so the app knows they're in an org now
              await dispatch(getMe())
            } else {
              // if there's no invite to accept, set the org invite error banner redux state to true
              dispatch(createUpdateOrgInviteErrorAction(true))
            }
            // set acceptOrgInvite to false
            dispatch(createUpdateAcceptOrgInviteAction(false))
          }
          dispatch(push(authedRedirectLocation))
          return postSigninSuccessActionDispatched
        },
        async (err) => {
          return dispatch(createPostSigninFailureAction(err.response.data))
        },
      )
  }
}

export const POST_AMPLIFY_SIGNIN_INIT = 'auth/POST_AMPLIFY_SIGNIN_INIT'
export const POST_AMPLIFY_SIGNIN_SUCCESS = 'auth/POST_AMPLIFY_SIGNIN_SUCCESS'
export const POST_AMPLIFY_SIGNIN_FAILURE = 'auth/POST_AMPLIFY_SIGNIN_FAILURE'
export const INIT_MFA_CHALLENGE = 'auth/INIT_MFA_CHALLENGE'
export const ABANDON_MFA_CHALLENGE = 'auth/ABANDON_MFA_CHALLENGE'
export const RESET_MFA_CODE_STATE_ACTION = 'auth/RESET_MFA_CODE_STATE_ACTION'
export const INIT_RESET_PASSWORD = 'auth/INIT_RESET_PASSWORD'

export interface PostAmplifySigninInitAction {
  type: typeof POST_AMPLIFY_SIGNIN_INIT
}
export interface PostAmplifySigninSuccessAction {
  type: typeof POST_AMPLIFY_SIGNIN_SUCCESS
  payload: { jwt: string | undefined }
}
export interface PostAmplifySigninFailureAction {
  type: typeof POST_AMPLIFY_SIGNIN_FAILURE
  payload: string
}
export interface InitMfaChallengeAction {
  type: typeof INIT_MFA_CHALLENGE
  payload: AuthCodeDeliveryDetails | undefined
}
export interface AbandonMfaChallengeAction {
  type: typeof ABANDON_MFA_CHALLENGE
}
export interface ResetMfaCodeStateAction {
  type: typeof RESET_MFA_CODE_STATE_ACTION
}
export interface InitResetPasswordAction {
  type: typeof INIT_RESET_PASSWORD
}

export type PostAmplifySigninActions =
  | PostAmplifySigninInitAction
  | PostAmplifySigninSuccessAction
  | PostAmplifySigninFailureAction
  | InitMfaChallengeAction
  | AbandonMfaChallengeAction
  | ResetMfaCodeStateAction
  | InitResetPasswordAction

const createPostAmplifySigninInitAction = (): PostAmplifySigninInitAction => ({
  type: POST_AMPLIFY_SIGNIN_INIT,
})
const createPostAmplifySigninSuccessAction = (payload: {
  jwt: string | undefined
}): PostAmplifySigninSuccessAction => ({
  type: POST_AMPLIFY_SIGNIN_SUCCESS,
  payload,
})
const createPostAmplifySigninFailureAction = (
  payload: string,
): PostAmplifySigninFailureAction => ({
  type: POST_AMPLIFY_SIGNIN_FAILURE,
  payload: payload,
})
const createInitMfaChallengeAction = (
  deliveryDetails: AuthCodeDeliveryDetails | undefined,
): InitMfaChallengeAction => ({
  type: INIT_MFA_CHALLENGE,
  payload: deliveryDetails,
})
export const createAbandonMfaChallengeAction =
  (): AbandonMfaChallengeAction => ({
    type: ABANDON_MFA_CHALLENGE,
  })
export const createResetMfaCodeStateAction = (): ResetMfaCodeStateAction => ({
  type: RESET_MFA_CODE_STATE_ACTION,
})
export const createInitResetPasswordAction = (): InitResetPasswordAction => ({
  type: INIT_RESET_PASSWORD,
})

export const postAmplifySignin = (
  email: string,
  password?: string,
  newCode?: boolean,
  csp?: boolean,
  acceptOrgInvite?: boolean,
): ThunkAction<
  Promise<
    PostAmplifySigninSuccessAction | PostAmplifySigninFailureAction | undefined
  >,
  RootState,
  void,
  | PostAmplifySigninActions
  | CallHistoryMethodAction
  | ConfirmCognitoSigninSuccessAction
  | ConfirmCspCognitoSigninSuccessAction
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      | PostAmplifySigninActions
      | CallHistoryMethodAction
      | ConfirmCognitoSigninSuccessAction
      | ConfirmCspCognitoSigninSuccessAction
      | SetEmailAction
    >,
    getState: () => RootState,
  ): Promise<
    PostAmplifySigninSuccessAction | PostAmplifySigninFailureAction | undefined
  > => {
    const authType = selectAuthTypeFromState(getState())
    dispatch(createPostAmplifySigninInitAction())

    if (csp && authType === AuthTypes.auth0) {
      dispatch(postCspLoginIssuerInfo(email, password))
    }

    // in amplify v6, attempting to call signIn with an existing session will throw an error
    try {
      const session = await fetchAuthSession()
      if (session.tokens !== undefined) {
        /* eslint-disable-next-line no-console */
        console.warn('session already existed', session)
        await signOut()
      }
    } catch {
      // do nothing
    }

    const locale = selectNullSafeLocaleFromState(getState())

    try {
      const brandId = selectBrandId(getState())
      let response = await signIn({
        username: email,
        password: password,
        options: { clientMetadata: { brandId, locale } },
      })

      // if a cognito user does not have an MFA preference and a sign in is deemed risky, cognito will require MFA but need to know
      // whether to send an Email or SMS since both are enabled on the user group
      // this should only happen for staging QA accounts where we use admin endpoints to manually set the user's MFA preference
      if (
        response.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION'
      ) {
        response = await confirmSignIn({
          challengeResponse: MfaDeliveryMedium.Email,
        })
      }

      const { isSignedIn, nextStep } = response

      if (!isSignedIn) {
        switch (nextStep.signInStep) {
          case 'CONFIRM_SIGN_IN_WITH_SMS_CODE': {
            dispatch(createInitMfaChallengeAction(nextStep.codeDeliveryDetails))
            break
          }
          case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE': {
            dispatch(
              createInitMfaChallengeAction({
                deliveryMedium: MfaDeliveryMedium.Email,
                destination: email, // using nextStep.codeDeliveryDetails.destination gives a partially retracted email
              } as AuthCodeDeliveryDetails),
            )

            break
          }

          case 'RESET_PASSWORD':
          case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED': {
            // This action will set the 'isSigningIn' back to false
            dispatch(createInitResetPasswordAction())
            dispatch(createSetEmail(email))
            dispatch(
              connectedPush({
                pathname: resetPasswordUrl(),
                search: 'mustResetPassword=true',
                state: {},
                hash: '',
              }),
            )

            // There's a bunch of stuff that happens after this that
            // I don't want happening. Return instead
            return
          }
          case 'DONE': {
            dispatch(getCognitoSigninThenPush(csp))
            break
          }
          case 'CONFIRM_SIGN_UP': {
            // User hasn't verified their email yet
            // Redirect to verification page
            dispatch(createSetEmail(email))
            dispatch(
              connectedPush({
                pathname: verifyEmailUrl(),
                state: { email, password },
                search: '',
                hash: '',
              }),
            )
            return
          }
          default:
            /* eslint-disable-next-line no-console */
            console.warn('postAmplifySignin: unexpected next step', nextStep)
            break
        }
      } else {
        const { tokens } = await fetchAuthSession()
        if (csp) {
          dispatch(
            createConfirmCspCognitoSigninSuccessAction({
              jwt: tokens?.accessToken.toString(),
            }),
          )
        } else {
          dispatch(
            createConfirmCognitoSigninSuccessAction({
              jwt: tokens?.accessToken.toString(),
            }),
          )
        }
        if (acceptOrgInvite) {
          dispatch(getOrganizationInvites())
        }
        await dispatch(getCognitoSigninThenPush(csp, acceptOrgInvite))
      }
      if (newCode) {
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: <Trns source={'New code sent!'} />,
          }),
        )
      }

      const { tokens } = await fetchAuthSession()
      return dispatch(
        createPostAmplifySigninSuccessAction({
          jwt: tokens?.accessToken.toString(),
        }),
      )
    } catch (error) {
      if (newCode) {
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: <Trns source={'Code could not be sent'} />,
            failureColorScheme: true,
          }),
        )
      }
      if (typeof error === 'string') {
        return dispatch(
          createPostAmplifySigninFailureAction(error.toUpperCase()),
        )
      } else if (error instanceof Error) {
        switch (error.name) {
          case 'UserLambdaValidationException':
            return dispatch(
              createPostAmplifySigninFailureAction(
                translateSingleString(
                  locale,
                  'Too many failed login attempts. Please try again later.',
                ),
              ),
            )
          default:
            return dispatch(createPostAmplifySigninFailureAction(error.message))
        }
      }
    }
  }
}

export const POST_CSP_LOGIN_INIT = 'auth/POST_CSP_LOGIN_INIT'
export const POST_CSP_LOGIN_SUCCESS = 'auth/POST_CSP_LOGIN_SUCCESS'
export const POST_CSP_LOGIN_FAILURE = 'auth/POST_CSP_LOGIN_FAILURE'

interface PostCspLoginSuccessPayload {
  issuers: IssuerListResponse[]
  refreshToken: string
  token: string
  user: UserListResponse
}

export interface PostCspLoginInitAction {
  type: typeof POST_CSP_LOGIN_INIT
}
export interface PostCspLoginSuccessAction {
  type: typeof POST_CSP_LOGIN_SUCCESS
  payload: PostCspLoginSuccessPayload
}
export interface PostCspLoginFailureAction {
  type: typeof POST_CSP_LOGIN_FAILURE
  payload: BackendErrorPayload<string>
}

export type PostCspLoginActions =
  | PostCspLoginInitAction
  | PostCspLoginSuccessAction
  | PostCspLoginFailureAction

const createPostCspLoginInitAction = (): PostCspLoginInitAction => ({
  type: POST_CSP_LOGIN_INIT,
})
const createPostCspLoginSuccessAction = (
  payload: PostCspLoginSuccessPayload,
): PostCspLoginSuccessAction => ({
  type: POST_CSP_LOGIN_SUCCESS,
  payload,
})
const createPostCspLoginFailureAction = (
  payload: BackendErrorPayload<string>,
): PostCspLoginFailureAction => ({
  type: POST_CSP_LOGIN_FAILURE,
  payload: payload,
})

export const postCspLogin = (
  email: string,
  password?: string,
): ThunkAction<
  Promise<PostCspLoginSuccessAction | PostCspLoginFailureAction>,
  RootState,
  void,
  PostCspLoginActions | CallHistoryMethodAction
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      PostCspLoginActions | CallHistoryMethodAction
    >,
    getState: () => RootState,
  ): Promise<PostCspLoginSuccessAction | PostCspLoginFailureAction> => {
    dispatch(createPostCspLoginInitAction())
    return axios
      .post(
        `/csp/login`,
        { email, password },
        selectUnauthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(createPostCspLoginSuccessAction(response.data))
        },
        async (err) => {
          return dispatch(createPostCspLoginFailureAction(err.response.data))
        },
      )
  }
}

export const POST_CSP_LOGIN_ISSUER_INFO_INIT =
  'auth/POST_CSP_LOGIN_ISSUER_INFO_INIT'
export const POST_CSP_LOGIN_ISSUER_INFO_SUCCESS =
  'auth/POST_CSP_LOGIN_ISSUER_INFO_SUCCESS'
export const POST_CSP_LOGIN_ISSUER_INFO_FAILURE =
  'auth/POST_CSP_LOGIN_ISSUER_INFO_FAILURE'

export interface PostCspLoginIssuerInfoInitAction {
  type: typeof POST_CSP_LOGIN_ISSUER_INFO_INIT
}
// reusing PostCspLoginSuccessPayload because we're hitting the same endpoint as postCspLogin
export interface PostCspLoginIssuerInfoSuccessAction {
  type: typeof POST_CSP_LOGIN_ISSUER_INFO_SUCCESS
  payload: PostCspLoginSuccessPayload
}
export interface PostCspLoginIssuerInfoFailureAction {
  type: typeof POST_CSP_LOGIN_ISSUER_INFO_FAILURE
  payload: BackendErrorPayload<string>
}

export type PostCspLoginIssuerInfoActions =
  | PostCspLoginIssuerInfoInitAction
  | PostCspLoginIssuerInfoSuccessAction
  | PostCspLoginIssuerInfoFailureAction

const createPostCspLoginIssuerInfoInitAction =
  (): PostCspLoginIssuerInfoInitAction => ({
    type: POST_CSP_LOGIN_ISSUER_INFO_INIT,
  })
const createPostCspLoginIssuerInfoSuccessAction = (
  payload: PostCspLoginSuccessPayload,
): PostCspLoginIssuerInfoSuccessAction => ({
  type: POST_CSP_LOGIN_ISSUER_INFO_SUCCESS,
  payload,
})
const createPostCspLoginIssuerInfoFailureAction = (
  payload: BackendErrorPayload<string>,
): PostCspLoginIssuerInfoFailureAction => ({
  type: POST_CSP_LOGIN_ISSUER_INFO_FAILURE,
  payload: payload,
})

export const postCspLoginIssuerInfo = (
  email: string,
  password?: string,
): ThunkAction<
  Promise<
    PostCspLoginIssuerInfoSuccessAction | PostCspLoginIssuerInfoFailureAction
  >,
  RootState,
  void,
  PostCspLoginIssuerInfoActions | CallHistoryMethodAction
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      PostCspLoginIssuerInfoActions | CallHistoryMethodAction
    >,
    getState: () => RootState,
  ): Promise<
    PostCspLoginIssuerInfoSuccessAction | PostCspLoginIssuerInfoFailureAction
  > => {
    dispatch(createPostCspLoginIssuerInfoInitAction())
    return axios
      .post(
        `/csp/login`,
        { email, password },
        selectUnauthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(
            createPostCspLoginIssuerInfoSuccessAction(response.data),
          )
        },
        async (err) => {
          return dispatch(
            createPostCspLoginIssuerInfoFailureAction(err.response.data),
          )
        },
      )
  }
}

export const POST_CSP_EMULATED_SESSIONS_OFFLINE_INIT =
  'auth/POST_CSP_EMULATED_SESSIONS_OFFLINE_INIT'
export const POST_CSP_EMULATED_SESSIONS_OFFLINE_SUCCESS =
  'auth/POST_CSP_EMULATED_SESSIONS_OFFLINE_SUCCESS'
export const POST_CSP_EMULATED_SESSIONS_OFFLINE_FAILURE =
  'auth/POST_CSP_EMULATED_SESSIONS_OFFLINE_FAILURE'

interface PostCspEmulatedSessionsOfflineSuccessPayload {
  token: EmulatedUserSessionTokenListResponse
}

export interface PostCspEmulatedSessionsOfflineInitAction {
  type: typeof POST_CSP_EMULATED_SESSIONS_OFFLINE_INIT
}
export interface PostCspEmulatedSessionsOfflineSuccessAction {
  type: typeof POST_CSP_EMULATED_SESSIONS_OFFLINE_SUCCESS
  payload: PostCspEmulatedSessionsOfflineSuccessPayload
  startTimeStamp: moment.Moment
}
export interface PostCspEmulatedSessionsOfflineFailureAction {
  type: typeof POST_CSP_EMULATED_SESSIONS_OFFLINE_FAILURE
  payload: BackendErrorPayload<string>
}

export type PostCspEmulatedSessionsOfflineActions =
  | PostCspEmulatedSessionsOfflineInitAction
  | PostCspEmulatedSessionsOfflineSuccessAction
  | PostCspEmulatedSessionsOfflineFailureAction

const createPostCspEmulatedSessionsOfflineInitAction =
  (): PostCspEmulatedSessionsOfflineInitAction => ({
    type: POST_CSP_EMULATED_SESSIONS_OFFLINE_INIT,
  })
const createPostCspEmulatedSessionsOfflineSuccessAction = (
  payload: PostCspEmulatedSessionsOfflineSuccessPayload,
  startTimeStamp: moment.Moment,
): PostCspEmulatedSessionsOfflineSuccessAction => ({
  type: POST_CSP_EMULATED_SESSIONS_OFFLINE_SUCCESS,
  payload,
  startTimeStamp,
})
const createPostCspEmulatedSessionsOfflineFailureAction = (
  payload: BackendErrorPayload<string>,
): PostCspEmulatedSessionsOfflineFailureAction => ({
  type: POST_CSP_EMULATED_SESSIONS_OFFLINE_FAILURE,
  payload: payload,
})

export const postCspEmulatedSessionsOfflineThenPush = (
  email: string,
): ThunkAction<
  Promise<
    | PostCspEmulatedSessionsOfflineSuccessAction
    | PostCspEmulatedSessionsOfflineFailureAction
  >,
  RootState,
  void,
  PostCspEmulatedSessionsOfflineActions | CallHistoryMethodAction
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      PostCspEmulatedSessionsOfflineActions | CallHistoryMethodAction
    >,
    getState: () => RootState,
  ): Promise<
    | PostCspEmulatedSessionsOfflineSuccessAction
    | PostCspEmulatedSessionsOfflineFailureAction
  > => {
    dispatch(createPostCspEmulatedSessionsOfflineInitAction())
    return axios
      .post(
        `/csp/emulated_sessions/offline`,
        { email },
        selectCspAuthedIssuerIdJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          startTimeStamp = moment().utc()
          const authedRedirectLocation =
            selectAuthedRedirectLocationFromState(getState())
          const postCspEmulatedSessionsOfflineSuccessActionDispatched =
            dispatch(
              createPostCspEmulatedSessionsOfflineSuccessAction(
                response.data,
                startTimeStamp,
              ),
            )
          dispatch(push(authedRedirectLocation))
          return postCspEmulatedSessionsOfflineSuccessActionDispatched
        },
        async (err) => {
          return dispatch(
            createPostCspEmulatedSessionsOfflineFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const POST_CSP_EMULATED_SESSIONS_LIVE_INIT =
  'auth/POST_CSP_EMULATED_SESSIONS_LIVE_INIT'
export const POST_CSP_EMULATED_SESSIONS_LIVE_SUCCESS =
  'auth/POST_CSP_EMULATED_SESSIONS_LIVE_SUCCESS'
export const POST_CSP_EMULATED_SESSIONS_LIVE_FAILURE =
  'auth/POST_CSP_EMULATED_SESSIONS_LIVE_FAILURE'

interface PostCspEmulatedSessionsLiveSuccessPayload {
  token: EmulatedUserSessionTokenListResponse
}

export interface PostCspEmulatedSessionsLiveInitAction {
  type: typeof POST_CSP_EMULATED_SESSIONS_LIVE_INIT
}
export interface PostCspEmulatedSessionsLiveSuccessAction {
  type: typeof POST_CSP_EMULATED_SESSIONS_LIVE_SUCCESS
  payload: PostCspEmulatedSessionsLiveSuccessPayload
}
export interface PostCspEmulatedSessionsLiveFailureAction {
  type: typeof POST_CSP_EMULATED_SESSIONS_LIVE_FAILURE
  payload: BackendErrorPayload<string>
}

export type PostCspEmulatedSessionsLiveActions =
  | PostCspEmulatedSessionsLiveInitAction
  | PostCspEmulatedSessionsLiveSuccessAction
  | PostCspEmulatedSessionsLiveFailureAction

const createPostCspEmulatedSessionsLiveInitAction =
  (): PostCspEmulatedSessionsLiveInitAction => ({
    type: POST_CSP_EMULATED_SESSIONS_LIVE_INIT,
  })
const createPostCspEmulatedSessionsLiveSuccessAction = (
  payload: PostCspEmulatedSessionsLiveSuccessPayload,
): PostCspEmulatedSessionsLiveSuccessAction => ({
  type: POST_CSP_EMULATED_SESSIONS_LIVE_SUCCESS,
  payload,
})
const createPostCspEmulatedSessionsLiveFailureAction = (
  payload: BackendErrorPayload<string>,
): PostCspEmulatedSessionsLiveFailureAction => ({
  type: POST_CSP_EMULATED_SESSIONS_LIVE_FAILURE,
  payload: payload,
})

export const postCspEmulatedSessionsLive = (
  email: string,
): ThunkAction<
  Promise<
    | PostCspEmulatedSessionsLiveSuccessAction
    | PostCspEmulatedSessionsLiveFailureAction
  >,
  RootState,
  void,
  PostCspEmulatedSessionsLiveActions | CallHistoryMethodAction
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      PostCspEmulatedSessionsLiveActions | CallHistoryMethodAction
    >,
    getState: () => RootState,
  ): Promise<
    | PostCspEmulatedSessionsLiveSuccessAction
    | PostCspEmulatedSessionsLiveFailureAction
  > => {
    dispatch(createPostCspEmulatedSessionsLiveInitAction())
    return axios
      .post(
        `/csp/emulated_sessions/live`,
        { email },
        selectCspAuthedIssuerIdJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(
            createPostCspEmulatedSessionsLiveSuccessAction(response.data),
          )
        },
        async (err) => {
          return dispatch(
            createPostCspEmulatedSessionsLiveFailureAction(err.response.data),
          )
        },
      )
  }
}

export const PUT_CSP_EMULATED_SESSIONS_RESEND_INIT =
  'auth/PUT_CSP_EMULATED_SESSIONS_RESEND_INIT'
export const PUT_CSP_EMULATED_SESSIONS_RESEND_SUCCESS =
  'auth/PUT_CSP_EMULATED_SESSIONS_RESEND_SUCCESS'
export const PUT_CSP_EMULATED_SESSIONS_RESEND_FAILURE =
  'auth/PUT_CSP_EMULATED_SESSIONS_RESEND_FAILURE'

interface PutCspEmulatedSessionsResendSuccessPayload {
  token: EmulatedUserSessionTokenListResponse
}

export interface PutCspEmulatedSessionsResendInitAction {
  type: typeof PUT_CSP_EMULATED_SESSIONS_RESEND_INIT
}
export interface PutCspEmulatedSessionsResendSuccessAction {
  type: typeof PUT_CSP_EMULATED_SESSIONS_RESEND_SUCCESS
  payload: PutCspEmulatedSessionsResendSuccessPayload
}
export interface PutCspEmulatedSessionsResendFailureAction {
  type: typeof PUT_CSP_EMULATED_SESSIONS_RESEND_FAILURE
  payload: BackendErrorPayload<string>
}

export type PutCspEmulatedSessionsResendActions =
  | PutCspEmulatedSessionsResendInitAction
  | PutCspEmulatedSessionsResendSuccessAction
  | PutCspEmulatedSessionsResendFailureAction

const createPutCspEmulatedSessionsResendInitAction =
  (): PutCspEmulatedSessionsResendInitAction => ({
    type: PUT_CSP_EMULATED_SESSIONS_RESEND_INIT,
  })
const createPutCspEmulatedSessionsResendSuccessAction = (
  payload: PutCspEmulatedSessionsResendSuccessPayload,
): PutCspEmulatedSessionsResendSuccessAction => ({
  type: PUT_CSP_EMULATED_SESSIONS_RESEND_SUCCESS,
  payload,
})
const createPutCspEmulatedSessionsResendFailureAction = (
  payload: BackendErrorPayload<string>,
): PutCspEmulatedSessionsResendFailureAction => ({
  type: PUT_CSP_EMULATED_SESSIONS_RESEND_FAILURE,
  payload: payload,
})

export const putCspEmulatedSessionsResendThenPush = (
  tokenId: string,
): ThunkAction<
  Promise<
    | PutCspEmulatedSessionsResendSuccessAction
    | PutCspEmulatedSessionsResendFailureAction
  >,
  RootState,
  void,
  PutCspEmulatedSessionsResendActions | CallHistoryMethodAction
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      PutCspEmulatedSessionsResendActions
    >,
    getState: () => RootState,
  ): Promise<
    | PutCspEmulatedSessionsResendSuccessAction
    | PutCspEmulatedSessionsResendFailureAction
  > => {
    dispatch(createPutCspEmulatedSessionsResendInitAction())
    return axios
      .put(
        `/csp/emulated_sessions/${tokenId}/resend`,
        {},
        selectCspAuthedIssuerIdJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(
            createPutCspEmulatedSessionsResendSuccessAction(response.data),
          )
        },
        async (err) => {
          return dispatch(
            createPutCspEmulatedSessionsResendFailureAction(err.response.data),
          )
        },
      )
  }
}
export const PUT_CSP_EMULATED_SESSIONS_VERIFY_INIT =
  'auth/PUT_CSP_EMULATED_SESSIONS_VERIFY_INIT'
export const PUT_CSP_EMULATED_SESSIONS_VERIFY_SUCCESS =
  'auth/PUT_CSP_EMULATED_SESSIONS_VERIFY_SUCCESS'
export const PUT_CSP_EMULATED_SESSIONS_VERIFY_FAILURE =
  'auth/PUT_CSP_EMULATED_SESSIONS_VERIFY_FAILURE'

interface PutCspEmulatedSessionsVerifySuccessPayload {
  token: EmulatedUserSessionTokenListResponse
}

export interface PutCspEmulatedSessionsVerifyInitAction {
  type: typeof PUT_CSP_EMULATED_SESSIONS_VERIFY_INIT
}
export interface PutCspEmulatedSessionsVerifySuccessAction {
  type: typeof PUT_CSP_EMULATED_SESSIONS_VERIFY_SUCCESS
  payload: PutCspEmulatedSessionsVerifySuccessPayload
  startTimeStamp: moment.Moment
}
export interface PutCspEmulatedSessionsVerifyFailureAction {
  type: typeof PUT_CSP_EMULATED_SESSIONS_VERIFY_FAILURE
  payload: BackendErrorPayload<string>
}

export type PutCspEmulatedSessionsVerifyActions =
  | PutCspEmulatedSessionsVerifyInitAction
  | PutCspEmulatedSessionsVerifySuccessAction
  | PutCspEmulatedSessionsVerifyFailureAction

const createPutCspEmulatedSessionsVerifyInitAction =
  (): PutCspEmulatedSessionsVerifyInitAction => ({
    type: PUT_CSP_EMULATED_SESSIONS_VERIFY_INIT,
  })
const createPutCspEmulatedSessionsVerifySuccessAction = (
  payload: PutCspEmulatedSessionsVerifySuccessPayload,
  startTimeStamp: moment.Moment,
): PutCspEmulatedSessionsVerifySuccessAction => ({
  type: PUT_CSP_EMULATED_SESSIONS_VERIFY_SUCCESS,
  payload,
  startTimeStamp,
})
const createPutCspEmulatedSessionsVerifyFailureAction = (
  payload: BackendErrorPayload<string>,
): PutCspEmulatedSessionsVerifyFailureAction => ({
  type: PUT_CSP_EMULATED_SESSIONS_VERIFY_FAILURE,
  payload: payload,
})

export const putCspEmulatedSessionsVerifyThenPush = (
  tokenId: string,
  verificationCode: string,
): ThunkAction<
  Promise<
    | PutCspEmulatedSessionsVerifySuccessAction
    | PutCspEmulatedSessionsVerifyFailureAction
  >,
  RootState,
  void,
  PutCspEmulatedSessionsVerifyActions | CallHistoryMethodAction
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      PutCspEmulatedSessionsVerifyActions | CallHistoryMethodAction
    >,
    getState: () => RootState,
  ): Promise<
    | PutCspEmulatedSessionsVerifySuccessAction
    | PutCspEmulatedSessionsVerifyFailureAction
  > => {
    dispatch(createPutCspEmulatedSessionsVerifyInitAction())
    return axios
      .put(
        `/csp/emulated_sessions/${tokenId}/verify`,
        { verificationCode },
        selectCspAuthedIssuerIdJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          startTimeStamp = moment().utc()
          const authedRedirectLocation =
            selectAuthedRedirectLocationFromState(getState())
          const putCspEmulatedSessionsVerifySuccessActionDispatched = dispatch(
            createPutCspEmulatedSessionsVerifySuccessAction(
              response.data,
              startTimeStamp,
            ),
          )
          dispatch(push(authedRedirectLocation))
          return putCspEmulatedSessionsVerifySuccessActionDispatched
        },
        async (err) => {
          return dispatch(
            createPutCspEmulatedSessionsVerifyFailureAction(err.response.data),
          )
        },
      )
  }
}

export const DELETE_CSP_EMULATED_SESSIONS_INIT =
  'auth/DELETE_CSP_EMULATED_SESSIONS_INIT'
export const DELETE_CSP_EMULATED_SESSIONS_SUCCESS =
  'auth/DELETE_CSP_EMULATED_SESSIONS_SUCCESS'
export const DELETE_CSP_EMULATED_SESSIONS_FAILURE =
  'auth/DELETE_CSP_EMULATED_SESSIONS_FAILURE'

interface DeleteCspEmulatedSessionsSuccessPayload {
  authedRedirectLocation: Location
  didSessionExpireAutomatically: boolean
}

export interface DeleteCspEmulatedSessionsInitAction {
  type: typeof DELETE_CSP_EMULATED_SESSIONS_INIT
}
export interface DeleteCspEmulatedSessionsSuccessAction {
  type: typeof DELETE_CSP_EMULATED_SESSIONS_SUCCESS
  payload: DeleteCspEmulatedSessionsSuccessPayload
}
export interface DeleteCspEmulatedSessionsFailureAction {
  type: typeof DELETE_CSP_EMULATED_SESSIONS_FAILURE
  payload: BackendErrorPayload<string> | string
}

export type DeleteCspEmulatedSessionsActions =
  | DeleteCspEmulatedSessionsInitAction
  | DeleteCspEmulatedSessionsSuccessAction
  | DeleteCspEmulatedSessionsFailureAction

const createDeleteCspEmulatedSessionsInitAction =
  (): DeleteCspEmulatedSessionsInitAction => ({
    type: DELETE_CSP_EMULATED_SESSIONS_INIT,
  })
const createDeleteCspEmulatedSessionsSuccessAction = (
  payload: DeleteCspEmulatedSessionsSuccessPayload,
): DeleteCspEmulatedSessionsSuccessAction => ({
  type: DELETE_CSP_EMULATED_SESSIONS_SUCCESS,
  payload,
})
const createDeleteCspEmulatedSessionsFailureAction = (
  payload: BackendErrorPayload<string> | string,
): DeleteCspEmulatedSessionsFailureAction => ({
  type: DELETE_CSP_EMULATED_SESSIONS_FAILURE,
  payload: payload,
})

export const deleteCspEmulatedSessions = (
  didSessionExpireAutomatically: boolean,
  shouldDeleteSignout: boolean,
  analytics: ExtendAnalyticsBrowser,
): ThunkAction<
  Promise<
    | DeleteCspEmulatedSessionsSuccessAction
    | DeleteCspEmulatedSessionsFailureAction
    | undefined
  >,
  RootState,
  void,
  DeleteCspEmulatedSessionsActions | CallHistoryMethodAction
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      DeleteCspEmulatedSessionsActions | CallHistoryMethodAction
    >,
    getState: () => RootState,
  ): Promise<
    | DeleteCspEmulatedSessionsSuccessAction
    | DeleteCspEmulatedSessionsFailureAction
    | undefined
  > => {
    dispatch(createDeleteCspEmulatedSessionsInitAction())
    const tokenId = selectUserSessionTokenIdFromState(getState())
    const authType = selectAuthTypeFromState(getState())
    return axios
      .delete(
        `/csp/emulated_sessions/${tokenId}`,
        selectCspAuthedIssuerIdJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          if (shouldDeleteSignout) {
            const a =
              authType === AuthTypes.auth0
                ? await dispatch(
                    deleteSignout(
                      didSessionExpireAutomatically,
                      analytics,
                      true,
                      undefined,
                    ),
                  )
                : await dispatch(
                    deleteCognitoSignout(
                      didSessionExpireAutomatically,
                      analytics,
                      true,
                    ),
                  )
            if (
              a?.type === DELETE_SIGNOUT_SUCCESS ||
              a?.type === DELETE_COGNITO_SIGNOUT_SUCCESS
            ) {
              const b = dispatch(
                createDeleteCspEmulatedSessionsSuccessAction({
                  ...response.data,
                  // authedRedirectLocation: didSessionExpireAutomatically
                  //   ? selectLocationFromState(state)
                  //   : homeLocation(),
                  didSessionExpireAutomatically: didSessionExpireAutomatically,
                }),
              )
              await dispatch(connectedPush(cspSigninUrl()))
              return b
            } else if (a !== undefined) {
              return dispatch(
                createDeleteCspEmulatedSessionsFailureAction(a.payload),
              )
            }
          } else {
            window.location.reload()
            return dispatch(
              createDeleteCspEmulatedSessionsSuccessAction({
                ...response.data,
                // authedRedirectLocation: didSessionExpireAutomatically
                //   ? selectLocationFromState(state)
                //   : homeLocation(),
                didSessionExpireAutomatically: didSessionExpireAutomatically,
              }),
            )
          }
        },
        async (err) => {
          return dispatch(
            createDeleteCspEmulatedSessionsFailureAction(err.response.data),
          )
        },
      )
  }
}

export const PUT_CSP_EMULATED_SESSIONS_REFRESH_INIT =
  'auth/PUT_CSP_EMULATED_SESSIONS_REFRESH_INIT'
export const PUT_CSP_EMULATED_SESSIONS_REFRESH_SUCCESS =
  'auth/PUT_CSP_EMULATED_SESSIONS_REFRESH_SUCCESS'
export const PUT_CSP_EMULATED_SESSIONS_REFRESH_FAILURE =
  'auth/PUT_CSP_EMULATED_SESSIONS_REFRESH_FAILURE'

interface PutCspEmulatedSessionsRefreshSuccessPayload {
  token: EmulatedUserSessionTokenListResponse
}

export interface PutCspEmulatedSessionsRefreshInitAction {
  type: typeof PUT_CSP_EMULATED_SESSIONS_REFRESH_INIT
}
export interface PutCspEmulatedSessionsRefreshSuccessAction {
  type: typeof PUT_CSP_EMULATED_SESSIONS_REFRESH_SUCCESS
  payload: PutCspEmulatedSessionsRefreshSuccessPayload
}
export interface PutCspEmulatedSessionsRefreshFailureAction {
  type: typeof PUT_CSP_EMULATED_SESSIONS_REFRESH_FAILURE
  payload: BackendErrorPayload<string>
}

export type PutCspEmulatedSessionsRefreshActions =
  | PutCspEmulatedSessionsRefreshInitAction
  | PutCspEmulatedSessionsRefreshSuccessAction
  | PutCspEmulatedSessionsRefreshFailureAction

const createPutCspEmulatedSessionsRefreshInitAction =
  (): PutCspEmulatedSessionsRefreshInitAction => ({
    type: PUT_CSP_EMULATED_SESSIONS_REFRESH_INIT,
  })
const createPutCspEmulatedSessionsRefreshSuccessAction = (
  payload: PutCspEmulatedSessionsRefreshSuccessPayload,
): PutCspEmulatedSessionsRefreshSuccessAction => ({
  type: PUT_CSP_EMULATED_SESSIONS_REFRESH_SUCCESS,
  payload,
})
const createPutCspEmulatedSessionsRefreshFailureAction = (
  payload: BackendErrorPayload<string>,
): PutCspEmulatedSessionsRefreshFailureAction => ({
  type: PUT_CSP_EMULATED_SESSIONS_REFRESH_FAILURE,
  payload: payload,
})

export const putCspEmulatedSessionsRefresh = (): ThunkAction<
  Promise<
    | PutCspEmulatedSessionsRefreshSuccessAction
    | PutCspEmulatedSessionsRefreshFailureAction
  >,
  RootState,
  void,
  PutCspEmulatedSessionsRefreshActions | CallHistoryMethodAction
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      PutCspEmulatedSessionsRefreshActions | CallHistoryMethodAction
    >,
    getState: () => RootState,
  ): Promise<
    | PutCspEmulatedSessionsRefreshSuccessAction
    | PutCspEmulatedSessionsRefreshFailureAction
  > => {
    dispatch(createPutCspEmulatedSessionsRefreshInitAction())
    const tokenId = selectUserSessionTokenIdFromState(getState())
    return axios
      .put(
        `/csp/emulated_sessions/${tokenId}/refresh`,
        {},
        selectCspAuthedIssuerIdJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(
            createPutCspEmulatedSessionsRefreshSuccessAction(response.data),
          )
        },
        async (err) => {
          return dispatch(
            createPutCspEmulatedSessionsRefreshFailureAction(err.response.data),
          )
        },
      )
  }
}

export const POST_RENEW_AUTH_INIT = 'auth/POST_RENEW_AUTH_INIT'
export const POST_RENEW_AUTH_SUCCESS = 'auth/POST_RENEW_AUTH_SUCCESS'
export const POST_RENEW_AUTH_FAILURE = 'auth/POST_RENEW_AUTH_FAILURE'

interface PostRenewAuthSuccessPayload {
  token: string
  refreshToken: string
  user: UserListResponse
  isCsp?: boolean
}

export interface PostRenewAuthInitAction {
  type: typeof POST_RENEW_AUTH_INIT
}
export interface PostRenewAuthSuccessAction {
  type: typeof POST_RENEW_AUTH_SUCCESS
  payload: PostRenewAuthSuccessPayload
}
export interface PostRenewAuthFailureAction {
  type: typeof POST_RENEW_AUTH_FAILURE
  payload: BackendErrorPayload<string>
}

export type PostRenewAuthActions =
  | PostRenewAuthInitAction
  | PostRenewAuthSuccessAction
  | PostRenewAuthFailureAction

const createPostRenewAuthInitAction = (): PostRenewAuthInitAction => ({
  type: POST_RENEW_AUTH_INIT,
})
const createPostRenewAuthSuccessAction = (
  payload: PostRenewAuthSuccessPayload,
): PostRenewAuthSuccessAction => ({
  type: POST_RENEW_AUTH_SUCCESS,
  payload,
})
const createPostRenewAuthFailureAction = (
  payload: BackendErrorPayload<string>,
): PostRenewAuthFailureAction => ({
  type: POST_RENEW_AUTH_FAILURE,
  payload: payload,
})

// THIS ONLY RUNS FOR AUTH0 LOGINS.
// THIS IS BARELY USED
export const postAuth0RenewAuth = (
  email: string,
  isCsp?: boolean,
  closeSessionExpiringPopup?: boolean,
): ThunkAction<Promise<void>, RootState, void, PostRenewAuthActions> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      PostRenewAuthActions | SetIsSessionExpiringPopupOpenAction
    >,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostRenewAuthInitAction())
    const state = getState()
    const refreshToken = selectRefreshTokenFromState(state)
    const cspRefreshToken = selectCspRefreshTokenFromState(state)
    return axios
      .post(
        `/renewauth`,
        {
          refreshToken: isCsp ? cspRefreshToken : refreshToken,
          email: email,
        },
        selectUnauthedJsonApiRequestConfig(state),
      )
      .then(
        (response) => {
          const isAuthenticated = selectIsAuthenticatedFromState(state)
          dispatch(
            createPostRenewAuthSuccessAction({
              ...response.data,
              isCsp: isCsp,
            }),
          )
          if (closeSessionExpiringPopup) {
            dispatch(createSetIsSessionExpiringPopupOpen(false))
          }
          if (isCsp && isAuthenticated) {
            dispatch(putCspEmulatedSessionsRefresh())
          }
        },
        (err) => {
          dispatch(createPostRenewAuthFailureAction(err.response.data))
        },
      )
  }
}

export const POST_COGNITO_RENEW_AUTH_INIT = 'auth/POST_COGNITO_RENEW_AUTH_INIT'
export const POST_COGNITO_RENEW_AUTH_SUCCESS =
  'auth/POST_COGNITO_RENEW_AUTH_SUCCESS'
export const POST_COGNITO_RENEW_AUTH_FAILURE =
  'auth/POST_COGNITO_RENEW_AUTH_FAILURE'

interface PostCognitoRenewAuthSuccessPayload {
  token: string
  refreshToken?: string
  isCsp?: boolean
}

export interface PostCognitoRenewAuthInitAction {
  type: typeof POST_COGNITO_RENEW_AUTH_INIT
}
export interface PostCognitoRenewAuthSuccessAction {
  type: typeof POST_COGNITO_RENEW_AUTH_SUCCESS
  payload: PostCognitoRenewAuthSuccessPayload
}
export interface PostCognitoRenewAuthFailureAction {
  type: typeof POST_COGNITO_RENEW_AUTH_FAILURE
  payload: string
}

export type PostCognitoRenewAuthActions =
  | PostCognitoRenewAuthInitAction
  | PostCognitoRenewAuthSuccessAction
  | PostCognitoRenewAuthFailureAction

const createPostCognitoRenewAuthInitAction =
  (): PostCognitoRenewAuthInitAction => ({
    type: POST_COGNITO_RENEW_AUTH_INIT,
  })
const createPostCognitoRenewAuthSuccessAction = (
  payload: PostCognitoRenewAuthSuccessPayload,
): PostCognitoRenewAuthSuccessAction => ({
  type: POST_COGNITO_RENEW_AUTH_SUCCESS,
  payload,
})
const createPostCognitoRenewAuthFailureAction = (
  payload: string,
): PostCognitoRenewAuthFailureAction => ({
  type: POST_COGNITO_RENEW_AUTH_FAILURE,
  payload: payload,
})

export const postCognitoRenewAuth = (
  closeSessionExpiringPopup?: boolean,
  isCsp?: boolean,
): ThunkAction<Promise<void>, RootState, void, PostCognitoRenewAuthActions> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      PostCognitoRenewAuthActions | SetIsSessionExpiringPopupOpenAction
    >,
  ): Promise<void> => {
    dispatch(createPostCognitoRenewAuthInitAction())
    try {
      const { tokens } = await fetchAuthSession({ forceRefresh: true })
      // Amplify should always throw if the auth session isn't valid after attempting a refresh, but for type safety
      // and debugging visibility, we check for undefined and log the case here
      if (tokens === undefined) {
        /* eslint-disable-next-line no-console */
        console.error(
          'postCognitoRenewAuth: No tokens returned from successful Amplify.Auth.fetchAuthSession call',
        )
        dispatch(
          createPostCognitoRenewAuthFailureAction(
            'No tokens returned from session refresh',
          ),
        )
        return
      }
      dispatch(
        createPostCognitoRenewAuthSuccessAction({
          token: tokens?.accessToken.toString(),
          isCsp: isCsp,
        }),
      )
      if (isCsp) {
        dispatch(putCspEmulatedSessionsRefresh())
      }
      if (closeSessionExpiringPopup) {
        dispatch(createSetIsSessionExpiringPopupOpen(false))
      }
    } catch (error) {
      if (typeof error === 'string') {
        dispatch(createPostCognitoRenewAuthFailureAction(error.toUpperCase()))
      } else if (error instanceof Error) {
        dispatch(createPostCognitoRenewAuthFailureAction(error.message))
      } else {
        // Handle unexpected error types to ensure isRenewingAuth is reset
        dispatch(
          createPostCognitoRenewAuthFailureAction(
            'Session refresh failed unexpectedly',
          ),
        )
      }
    }
  }
}

export const POST_RESET_PASSWORD_INIT = 'auth/POST_RESET_PASSWORD_INIT'
export const POST_RESET_PASSWORD_SUCCESS = 'auth/POST_RESET_PASSWORD_SUCCESS'
export const POST_RESET_PASSWORD_FAILURE = 'auth/POST_RESET_PASSWORD_FAILURE'

interface PostResetPasswordSuccessPayload {}

export interface PostResetPasswordInitAction {
  type: typeof POST_RESET_PASSWORD_INIT
}
export interface PostResetPasswordSuccessAction {
  type: typeof POST_RESET_PASSWORD_SUCCESS
  payload: PostResetPasswordSuccessPayload
}
export interface PostResetPasswordFailureAction {
  type: typeof POST_RESET_PASSWORD_FAILURE
  payload: BackendErrorPayload<string>
}

export type PostResetPasswordActions =
  | PostResetPasswordInitAction
  | PostResetPasswordSuccessAction
  | PostResetPasswordFailureAction

const createPostResetPasswordInitAction = (): PostResetPasswordInitAction => ({
  type: POST_RESET_PASSWORD_INIT,
})
const createPostResetPasswordSuccessAction = (
  payload: PostResetPasswordSuccessPayload,
): PostResetPasswordSuccessAction => ({
  type: POST_RESET_PASSWORD_SUCCESS,
  payload,
})
const createPostResetPasswordFailureAction = (
  payload: BackendErrorPayload<string>,
): PostResetPasswordFailureAction => ({
  type: POST_RESET_PASSWORD_FAILURE,
  payload: payload,
})

export const postResetPassword = (
  email: string,
  openToast = false,
): ThunkAction<Promise<void>, RootState, void, PostResetPasswordActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostResetPasswordActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostResetPasswordInitAction())
    return axios
      .post(
        `/forgot`,
        { email },
        selectUnauthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          if (openToast) {
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: <PostResetPasswordToastMessage email={email} />,
              }),
            )
          }
          dispatch(createPostResetPasswordSuccessAction(response.data))
        },
        (err) => {
          dispatch(createPostResetPasswordFailureAction(err.response.data))
        },
      )
  }
}

export const DELETE_SIGNOUT_INIT = 'auth/DELETE_SIGNOUT_INIT'
export const DELETE_SIGNOUT_SUCCESS = 'auth/DELETE_SIGNOUT_SUCCESS'
export const DELETE_SIGNOUT_FAILURE = 'auth/DELETE_SIGNOUT_FAILURE'

export type DeleteSignoutSuccessPayload = {
  authedRedirectLocation: Location
  didSessionExpireAutomatically: boolean
  isCsp?: boolean
}
export type DeleteSignoutFailurePayload = BackendErrorPayload<string>

export interface DeleteSignoutInitAction {
  type: typeof DELETE_SIGNOUT_INIT
}
export interface DeleteSignoutSuccessAction {
  type: typeof DELETE_SIGNOUT_SUCCESS
  payload: DeleteSignoutSuccessPayload
}
export interface DeleteSignoutFailureAction {
  type: typeof DELETE_SIGNOUT_FAILURE
  payload: DeleteSignoutFailurePayload
}

export type DeleteSignoutActions =
  | DeleteSignoutInitAction
  | DeleteSignoutSuccessAction
  | DeleteSignoutFailureAction

const createDeleteSignoutInitAction = (): DeleteSignoutInitAction => ({
  type: DELETE_SIGNOUT_INIT,
})
const createDeleteSignoutSuccessAction = (
  payload: DeleteSignoutSuccessPayload,
): DeleteSignoutSuccessAction => ({
  type: DELETE_SIGNOUT_SUCCESS,
  payload,
})
const createDeleteSignoutFailureAction = (
  payload: DeleteSignoutFailurePayload,
): DeleteSignoutFailureAction => ({
  type: DELETE_SIGNOUT_FAILURE,
  payload,
})

export const deleteSignout = (
  didSessionExpireAutomatically: boolean,
  analytics: ExtendAnalyticsBrowser,
  isCsp?: boolean,
  calculatedBackgroundLocationPathName?: string,
): ThunkAction<
  Promise<DeleteSignoutFailureAction | DeleteSignoutSuccessAction>,
  RootState,
  void,
  DeleteSignoutActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, DeleteSignoutActions>,
    getState: () => RootState,
  ): Promise<DeleteSignoutFailureAction | DeleteSignoutSuccessAction> => {
    dispatch(createDeleteSignoutInitAction())
    const state = getState()
    const refreshToken = selectRefreshTokenFromState(state)
    const cspRefreshToken = selectCspRefreshTokenFromState(state)
    return axios
      .delete(`/signout`, {
        ...(isCsp
          ? selectCspAuthedJsonApiRequestConfig(state)
          : selectAuthedJsonApiRequestConfig(state)),
        data: {
          refreshToken: isCsp ? cspRefreshToken : refreshToken,
        },
      })
      .then(
        async (response) => {
          const endSessionTimeStamp = moment().utc()
          const dif = moment.duration(
            endSessionTimeStamp.diff(getStartTimeStamp()),
          )

          analytics?.reset()

          !isCsp &&
            analytics?.track(
              didSessionExpireAutomatically
                ? 'user session - automatically logged out - inactivity'
                : 'user session -  logged out - text link press',
              {
                intent: 'log out',
                lastTargetUrl: calculatedBackgroundLocationPathName,
                endSessionTimeStamp: endSessionTimeStamp.format(
                  'MM/DD/YYYY hh:mm:ss a',
                ),
                startSessionTimeStamp: getStartTimeStamp()?.format(
                  'MM/DD/YYYY hh:mm:ss a',
                ),
                sessionDuration: [
                  dif.hours(),
                  dif.minutes(),
                  dif.seconds(),
                ].join(':'),
              },
            )
          return dispatch(
            createDeleteSignoutSuccessAction({
              ...response.data,
              authedRedirectLocation: didSessionExpireAutomatically
                ? selectLocationFromState(state)
                : homeLocation(),
              didSessionExpireAutomatically: didSessionExpireAutomatically,
              isCsp: isCsp,
            }),
          )
        },
        (err) => {
          return dispatch(createDeleteSignoutFailureAction(err.response.data))
        },
      )
  }
}

export const DELETE_COGNITO_SIGNOUT_INIT = 'auth/DELETE_COGNITO_SIGNOUT_INIT'
export const DELETE_COGNITO_SIGNOUT_SUCCESS =
  'auth/DELETE_COGNITO_SIGNOUT_SUCCESS'
export const DELETE_COGNITO_SIGNOUT_FAILURE =
  'auth/DELETE_COGNITO_SIGNOUT_FAILURE'

export type DeleteCognitoSignoutSuccessPayload = {
  authedRedirectLocation: Location
  didSessionExpireAutomatically: boolean
  isCsp?: boolean
}
export interface DeleteCognitoSignoutInitAction {
  type: typeof DELETE_COGNITO_SIGNOUT_INIT
}
export interface DeleteCognitoSignoutSuccessAction {
  type: typeof DELETE_COGNITO_SIGNOUT_SUCCESS
  payload: DeleteCognitoSignoutSuccessPayload
}
export interface DeleteCognitoSignoutFailureAction {
  type: typeof DELETE_COGNITO_SIGNOUT_FAILURE
  payload: string
}

export type DeleteCognitoSignoutActions =
  | DeleteCognitoSignoutInitAction
  | DeleteCognitoSignoutSuccessAction
  | DeleteCognitoSignoutFailureAction

const createDeleteCognitoSignoutInitAction =
  (): DeleteCognitoSignoutInitAction => ({
    type: DELETE_COGNITO_SIGNOUT_INIT,
  })
const createDeleteCognitoSignoutSuccessAction = (
  payload: DeleteCognitoSignoutSuccessPayload,
): DeleteCognitoSignoutSuccessAction => ({
  type: DELETE_COGNITO_SIGNOUT_SUCCESS,
  payload,
})
const createDeleteCognitoSignoutFailureAction = (
  payload: string,
): DeleteCognitoSignoutFailureAction => ({
  type: DELETE_COGNITO_SIGNOUT_FAILURE,
  payload,
})

export const deleteCognitoSignout = (
  didSessionExpireAutomatically: boolean,
  analytics: ExtendAnalyticsBrowser,
  isCsp?: boolean,
): ThunkAction<
  Promise<
    | DeleteCognitoSignoutSuccessAction
    | DeleteCognitoSignoutFailureAction
    | undefined
  >,
  RootState,
  void,
  DeleteCognitoSignoutActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, DeleteCognitoSignoutActions>,
    getState: () => RootState,
  ): Promise<
    | DeleteCognitoSignoutSuccessAction
    | DeleteCognitoSignoutFailureAction
    | undefined
  > => {
    dispatch(createDeleteCognitoSignoutInitAction())
    const state = getState()
    const env = selectEnvironmentFromState(state)
    try {
      await signOut()

      // Clear SSO user pool cookies to prevent stale config on next login
      const cookies = new Cookies()
      cookies.remove(getUserPoolClientIdCookieName(env), { path: '/' })
      cookies.remove(getUserPoolIdCookieName(env), { path: '/' })

      analytics?.reset()

      const deleteCognitoSignoutSuccessActionDispatched = dispatch(
        createDeleteCognitoSignoutSuccessAction({
          authedRedirectLocation: didSessionExpireAutomatically
            ? selectLocationFromState(state)
            : homeLocation(),
          didSessionExpireAutomatically: didSessionExpireAutomatically,
          isCsp: isCsp,
        }),
      )
      if (isCsp) {
        // redirect to csp login page
        await dispatch(connectedPush(cspSigninUrl()))
      }
      return deleteCognitoSignoutSuccessActionDispatched
    } catch (error) {
      if (typeof error === 'string') {
        return dispatch(
          createDeleteCognitoSignoutFailureAction(error.toUpperCase()),
        )
      } else if (error instanceof Error) {
        return dispatch(createDeleteCognitoSignoutFailureAction(error.message))
      }
    }
  }
}

export const POST_USER_VERIFICATION_INIT = 'auth/POST_USER_VERIFICATION_INIT'
export const POST_USER_VERIFICATION_SUCCESS =
  'auth/POST_USER_VERIFICATION_SUCCESS'
export const POST_USER_VERIFICATION_FAILURE =
  'auth/POST_USER_VERIFICATION_FAILURE'

interface PostUserVerificationSuccessPayload {
  msg: string
}

export interface PostUserVerificationInitAction {
  type: typeof POST_USER_VERIFICATION_INIT
}
export interface PostUserVerificationSuccessAction {
  type: typeof POST_USER_VERIFICATION_SUCCESS
  payload: PostUserVerificationSuccessPayload
}
export interface PostUserVerificationFailureAction {
  type: typeof POST_USER_VERIFICATION_FAILURE
  payload: BackendErrorPayload<string>
}

export type PostUserVerificationActions =
  | PostUserVerificationInitAction
  | PostUserVerificationSuccessAction
  | PostUserVerificationFailureAction

const createPostUserVerificationInitAction =
  (): PostUserVerificationInitAction => ({
    type: POST_USER_VERIFICATION_INIT,
  })
const createPostUserVerificationSuccessAction = (
  payload: PostUserVerificationSuccessPayload,
): PostUserVerificationSuccessAction => ({
  type: POST_USER_VERIFICATION_SUCCESS,
  payload: payload,
})
const createPostUserVerificationFailureAction = (
  payload: BackendErrorPayload<string>,
): PostUserVerificationFailureAction => ({
  type: POST_USER_VERIFICATION_FAILURE,
  payload: payload,
})

export const postUserVerification = (
  code: string,
  email: string,
): ThunkAction<
  Promise<
    PostUserVerificationSuccessAction | PostUserVerificationFailureAction
  >,
  RootState,
  void,
  PostUserVerificationActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostUserVerificationActions>,
    getState: () => RootState,
  ): Promise<
    PostUserVerificationSuccessAction | PostUserVerificationFailureAction
  > => {
    dispatch(createPostUserVerificationInitAction())
    return axios
      .post(
        `/users/verify`,
        { email, code },
        selectUnauthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          return dispatch(
            createPostUserVerificationSuccessAction(response.data),
          )
        },
        (err) => {
          return dispatch(
            createPostUserVerificationFailureAction(err.response.data),
          )
        },
      )
  }
}

export const postUserVerificationThenPostSignin = (
  code: string,
  email: string,
  password?: string,
  acceptOrgInvite?: boolean,
  suppressRedirect?: boolean,
): ThunkAction<Promise<void>, RootState, void, PostUserVerificationActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostUserVerificationActions>,
    getState: () => RootState,
  ): Promise<void> => {
    const state = getState()
    const env = selectEnvironmentFromState(state)
    const axiosConfig = selectUnauthedJsonApiRequestConfig(state)
    const featureFlagger = selectFeatureFlaggerFromState(state)
    const ssoEnabled = isSSOLoginEnabled(featureFlagger)

    dispatch(createPostUserVerificationInitAction())
    return axios.post(`/users/verify`, { email, code }, axiosConfig).then(
      async (response) => {
        const authType = selectAuthTypeFromState(getState())
        if (password) {
          // Reconfigure Amplify with the correct user pool before signing in
          // This ensures we're talking to the pool where the user was created
          try {
            await configureAmplify(env, {
              email,
              axiosConfig,
              ssoEnabled,
            })
          } catch {
            // If authconfig fails, continue with existing config
            // The sign-in may still work if the user is in the expected pool
          }

          if (authType === AuthTypes.cognito) {
            await dispatch(
              postAmplifySignin(email, password, false, false, acceptOrgInvite),
            )
          } else {
            await dispatch(postSigninThenPush(email, password, acceptOrgInvite))
          }
        }
        startTimeStamp = moment().utc()

        if (!suppressRedirect) {
          if (acceptOrgInvite) {
            dispatch(connectedPush(homeUrl()))
          } else {
            dispatch(connectedPush(onboardingUrl()))
          }
        }
        dispatch(createPostUserVerificationSuccessAction(response.data))
      },
      (err) => {
        dispatch(createPostUserVerificationFailureAction(err.response.data))
      },
    )
  }
}

export const postUserVerificationThenPostCspLogin = (
  code: string,
  email: string,
  password?: string,
  csp?: boolean,
): ThunkAction<Promise<void>, RootState, void, PostUserVerificationActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostUserVerificationActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostUserVerificationInitAction())
    return axios
      .post(
        `/users/verify`,
        { email, code },
        selectUnauthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          const authType = selectAuthTypeFromState(getState())
          if (password) {
            if (authType === AuthTypes.cognito) {
              await dispatch(postAmplifySignin(email, password, false, csp))
            } else {
              await dispatch(postCspLogin(email, password))
            }
          }
          dispatch(createPostUserVerificationSuccessAction(response.data))
        },
        (err) => {
          dispatch(createPostUserVerificationFailureAction(err.response.data))
        },
      )
  }
}

export const CONFIRM_COGNITO_SIGNIN_INIT = 'auth/CONFIRM_COGNITO_SIGNIN_INIT'
export const CONFIRM_COGNITO_SIGNIN_SUCCESS =
  'auth/CONFIRM_COGNITO_SIGNIN_SUCCESS'
export const CONFIRM_COGNITO_SIGNIN_FAILURE =
  'auth/CONFIRM_COGNITO_SIGNIN_FAILURE'
export const CSP_AUTHED_SUCCESS = 'auth/CSP_AUTHED_SUCCESS'
export const CONFIRM_CSP_COGNITO_SIGNIN_SUCCESS =
  'auth/CONFIRM_CSP_COGNITO_SIGNIN_SUCCESS'
export const POPULATE_USER_INFO_LEGACY = 'auth/POPULATE_USER_INFO_LEGACY'

export interface ConfirmCognitoSigninInitAction {
  type: typeof CONFIRM_COGNITO_SIGNIN_INIT
}
export interface ConfirmCognitoSigninSuccessAction {
  type: typeof CONFIRM_COGNITO_SIGNIN_SUCCESS
  payload: { jwt: string | undefined }
}
export interface ConfirmCognitoSigninFailureAction {
  type: typeof CONFIRM_COGNITO_SIGNIN_FAILURE
  payload: string
}

export interface ConfirmCspCognitoSigninSuccessAction {
  type: typeof CONFIRM_CSP_COGNITO_SIGNIN_SUCCESS
  payload: { jwt: string | undefined }
}

export interface CspAuthedSuccessAction {
  type: typeof CSP_AUTHED_SUCCESS
}

export interface PopulateUserInfoLegacyPayload {
  jwt: string
  userId: string
}

export interface PopulateUserInfoLegacyAction {
  type: typeof POPULATE_USER_INFO_LEGACY
  payload: PopulateUserInfoLegacyPayload
}

export type ConfirmCognitoSigninActions =
  | ConfirmCognitoSigninInitAction
  | ConfirmCognitoSigninSuccessAction
  | ConfirmCognitoSigninFailureAction
  | CspAuthedSuccessAction
  | ConfirmCspCognitoSigninSuccessAction
  | PopulateUserInfoLegacyAction

const createConfirmCognitoSigninInitAction =
  (): ConfirmCognitoSigninInitAction => ({
    type: CONFIRM_COGNITO_SIGNIN_INIT,
  })
const createConfirmCognitoSigninSuccessAction = (payload: {
  jwt: string | undefined
}): ConfirmCognitoSigninSuccessAction => ({
  type: CONFIRM_COGNITO_SIGNIN_SUCCESS,
  payload: payload,
})
const createConfirmCognitoSigninFailureAction = (
  payload: string,
): ConfirmCognitoSigninFailureAction => ({
  type: CONFIRM_COGNITO_SIGNIN_FAILURE,
  payload: payload,
})
const createCspAuthedSuccessAction = (): CspAuthedSuccessAction => ({
  type: CSP_AUTHED_SUCCESS,
})
const createConfirmCspCognitoSigninSuccessAction = (payload: {
  jwt: string | undefined
}): ConfirmCspCognitoSigninSuccessAction => ({
  type: CONFIRM_CSP_COGNITO_SIGNIN_SUCCESS,
  payload: payload,
})

export const createPopulateUserInfoLegacyAction = (
  payload: PopulateUserInfoLegacyPayload,
): PopulateUserInfoLegacyAction => ({
  type: POPULATE_USER_INFO_LEGACY,
  payload: payload,
})

export const confirmCognitoSignin = (
  code: string,
  rememberDevice?: boolean,
  csp?: boolean,
): ThunkAction<Promise<void>, RootState, void, ConfirmCognitoSigninActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, ConfirmCognitoSigninActions>,
    getState: () => RootState,
  ): Promise<void> => {
    const locale = selectNullSafeLocaleFromState(getState())
    dispatch(createConfirmCognitoSigninInitAction())
    try {
      await confirmSignIn({ challengeResponse: code })
      const { tokens } = await fetchAuthSession()
      if (csp) {
        dispatch(
          createConfirmCspCognitoSigninSuccessAction({
            jwt: tokens?.accessToken.toString(),
          }),
        )
      } else {
        dispatch(
          createConfirmCognitoSigninSuccessAction({
            jwt: tokens?.accessToken.toString(),
          }),
        )
      }
      if (rememberDevice) {
        dispatch(cognitoRememberDevice())
      }

      // not ideal but we have to set isCspAuthenticated to true before we hit getCspMe in getCognitoSigninThenPush
      if (csp) {
        dispatch(createCspAuthedSuccessAction())
      }

      dispatch(getCognitoSigninThenPush(csp))
    } catch (error) {
      if (typeof error === 'string') {
        dispatch(createConfirmCognitoSigninFailureAction(error.toUpperCase()))
      } else if (error instanceof Error) {
        let errorMessage: string

        switch (error.name) {
          case 'CodeMismatchException':
            errorMessage = translateSingleString(locale, 'Invalid code')
            break
          case 'NotAuthorizedException':
            errorMessage = translateSingleString(locale, 'Your session expired')
            break
          default:
            errorMessage = error.message
        }
        dispatch(createConfirmCognitoSigninFailureAction(errorMessage))
      }
    }
  }
}

export const cognitoRememberDevice = (): ThunkAction<
  Promise<void>,
  RootState,
  void,
  Action
> => {
  return async (): Promise<void> => {
    try {
      await rememberDevice()
    } catch (error) {
      /* eslint-disable-next-line no-console */
      console.error(error)
    }
  }
}

export const GET_COGNITO_SIGNIN_THEN_PUSH_INIT =
  'auth/GET_COGNITO_SIGNIN_THEN_PUSH_INIT'
export const GET_COGNITO_SIGNIN_THEN_PUSH_SUCCESS =
  'auth/GET_COGNITO_SIGNIN_THEN_PUSH_SUCCESS'
export const GET_COGNITO_SIGNIN_THEN_PUSH_FAILURE =
  'auth/GET_COGNITO_SIGNIN_THEN_PUSH_FAILURE'
export const GET_CSP_COGNITO_SIGNIN_THEN_PUSH_SUCCESS =
  'auth/GET_CSP_COGNITO_SIGNIN_THEN_PUSH_SUCCESS'

type GetCognitoSigninThenPushSuccessPayload = UserListResponse
type GetCspCognitoSigninThenPushSuccessPayload = UserListResponse

export interface GetCognitoSigninThenPushInitAction {
  type: typeof GET_COGNITO_SIGNIN_THEN_PUSH_INIT
}
export interface GetCognitoSigninThenPushSuccessAction {
  type: typeof GET_COGNITO_SIGNIN_THEN_PUSH_SUCCESS
  payload: GetCognitoSigninThenPushSuccessPayload
}
export interface GetCognitoSigninThenPushFailureAction {
  type: typeof GET_COGNITO_SIGNIN_THEN_PUSH_FAILURE
  payload: BackendErrorPayload<string>
}

export interface GetCspCognitoSigninThenPushSuccessAction {
  type: typeof GET_CSP_COGNITO_SIGNIN_THEN_PUSH_SUCCESS
  payload: GetCspCognitoSigninThenPushSuccessPayload
}

export type GetCognitoSigninThenPushActions =
  | GetCognitoSigninThenPushInitAction
  | GetCognitoSigninThenPushSuccessAction
  | GetCognitoSigninThenPushFailureAction
  | GetCspCognitoSigninThenPushSuccessAction

const createGetCognitoSigninThenPushInitAction =
  (): GetCognitoSigninThenPushInitAction => ({
    type: GET_COGNITO_SIGNIN_THEN_PUSH_INIT,
  })
const createGetCognitoSigninThenPushSuccessAction = (
  payload: GetCognitoSigninThenPushSuccessPayload,
): GetCognitoSigninThenPushSuccessAction => ({
  type: GET_COGNITO_SIGNIN_THEN_PUSH_SUCCESS,
  payload,
})
const createGetCognitoSigninThenPushFailureAction = (
  payload: BackendErrorPayload<string>,
): GetCognitoSigninThenPushFailureAction => ({
  type: GET_COGNITO_SIGNIN_THEN_PUSH_FAILURE,
  payload: payload,
})

const createGetCspCognitoSigninThenPushSuccessAction = (
  payload: GetCspCognitoSigninThenPushSuccessPayload,
): GetCspCognitoSigninThenPushSuccessAction => ({
  type: GET_CSP_COGNITO_SIGNIN_THEN_PUSH_SUCCESS,
  payload,
})

export const getCognitoSigninThenPush = (
  csp?: boolean,
  acceptOrgInvite?: boolean,
): ThunkAction<
  Promise<
    | GetCognitoSigninThenPushSuccessAction
    | GetCspCognitoSigninThenPushSuccessAction
    | GetCognitoSigninThenPushFailureAction
  >,
  RootState,
  void,
  GetCognitoSigninThenPushActions | CallHistoryMethodAction
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      | GetCognitoSigninThenPushActions
      | CallHistoryMethodAction
      | UpdateAcceptOrgInviteAction
      | UpdateOrgInviteErrorAction
    >,
    getState: () => RootState,
  ): Promise<
    | GetCognitoSigninThenPushSuccessAction
    | GetCspCognitoSigninThenPushSuccessAction
    | GetCognitoSigninThenPushFailureAction
  > => {
    dispatch(createGetCognitoSigninThenPushInitAction())

    let organizationInvites: unknown[]
    if (acceptOrgInvite) {
      await dispatch(getOrganizationInvites())
      organizationInvites = selectFilteredOrganizationInviteModels(getState())
    }

    const refreshUser = (): void => {
      if (csp) {
        dispatch(getCspMe())
      } else {
        dispatch(getMe())
      }
    }

    return axios
      .get(
        `/users/me`,
        csp
          ? selectCspAuthedJsonApiRequestConfig(getState())
          : selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          refreshUser()
          organizationInvites =
            selectFilteredOrganizationInviteModels(getState())

          if (acceptOrgInvite) {
            if (organizationInvites?.length > 0) {
              const firstOrgInvite = Object.values(
                organizationInvites,
              )[0] as OrganizationInviteModel

              dispatch(
                putOrganizationInvite(OrgInviteStatus.Accepted, firstOrgInvite),
              )

              dispatch(createUpdateAcceptOrgInviteAction(false))
              refreshUser()
              dispatch(connectedReplace(homeUrl()))
            } else {
              dispatch(createUpdateOrgInviteErrorAction(true))
            }
          }
          const getCognitoSigninThenPushSuccessActionDispatched = csp
            ? dispatch(
                createGetCspCognitoSigninThenPushSuccessAction(response.data),
              )
            : dispatch(
                createGetCognitoSigninThenPushSuccessAction(response.data),
              )

          const authedRedirectLocation =
            selectAuthedRedirectLocationFromState(getState())
          dispatch(push(authedRedirectLocation))
          return getCognitoSigninThenPushSuccessActionDispatched
        },
        (err) => {
          return dispatch(
            createGetCognitoSigninThenPushFailureAction(err.response.data),
          )
        },
      )
  }
}

export const POST_USER_RESEND_VERIFICATION_INIT =
  'auth/POST_USER_RESEND_VERIFICATION_INIT'
export const POST_USER_RESEND_VERIFICATION_SUCCESS =
  'auth/POST_USER_RESEND_VERIFICATION_SUCCESS'
export const POST_USER_RESEND_VERIFICATION_FAILURE =
  'auth/POST_USER_RESEND_VERIFICATION_FAILURE'

export interface PostUserResendVerificationSuccessPayload {}

export interface PostUserResendVerificationInitAction {
  type: typeof POST_USER_RESEND_VERIFICATION_INIT
}
export interface PostUserResendVerificationSuccessAction {
  type: typeof POST_USER_RESEND_VERIFICATION_SUCCESS
  payload: PostUserResendVerificationSuccessPayload
}
export interface PostUserResendVerificationFailureAction {
  type: typeof POST_USER_RESEND_VERIFICATION_FAILURE
  payload: BackendErrorPayload<string>
}

export type PostUserResendVerificationActions =
  | PostUserResendVerificationInitAction
  | PostUserResendVerificationSuccessAction
  | PostUserResendVerificationFailureAction

const createPostUserResendVerificationInitAction =
  (): PostUserResendVerificationInitAction => ({
    type: POST_USER_RESEND_VERIFICATION_INIT,
  })
const createPostUserResendVerificationSuccessAction = (
  payload: PostUserResendVerificationSuccessPayload,
): PostUserResendVerificationSuccessAction => ({
  type: POST_USER_RESEND_VERIFICATION_SUCCESS,
  payload,
})
const createPostUserResendVerificationFailureAction = (
  payload: BackendErrorPayload<string>,
): PostUserResendVerificationFailureAction => ({
  type: POST_USER_RESEND_VERIFICATION_FAILURE,
  payload: payload,
})

export const postUserResendVerification = (
  email: string,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  PostUserResendVerificationActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostUserResendVerificationActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostUserResendVerificationInitAction())
    return axios
      .post(
        `/users/resendverification`,
        { email },
        selectUnauthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPostUserResendVerificationSuccessAction(response.data))
        },
        (err) => {
          dispatch(
            createPostUserResendVerificationFailureAction(err.response.data),
          )
        },
      )
  }
}

export const POST_USER_INIT = 'auth/POST_USER_INIT'
export const POST_USER_SUCCESS = 'auth/POST_USER_SUCCESS'
export const POST_USER_FAILURE = 'auth/POST_USER_FAILURE'

interface PostUserSuccessPayload {
  user: UserListModel
}

export type PostUserFailurePayload = BackendErrorPayload<string>

export interface PostUserInitAction {
  type: typeof POST_USER_INIT
}
export interface PostUserSuccessAction {
  type: typeof POST_USER_SUCCESS
  payload: PostUserSuccessPayload
}
export interface PostUserFailureAction {
  type: typeof POST_USER_FAILURE
  payload: PostUserFailurePayload
}

export type PostUserActions =
  | PostUserInitAction
  | PostUserSuccessAction
  | PostUserFailureAction

const createPostUserInitAction = (): PostUserInitAction => ({
  type: POST_USER_INIT,
})
const createPostUserSuccessAction = (
  payload: PostUserSuccessPayload,
): PostUserSuccessAction => ({
  type: POST_USER_SUCCESS,
  payload,
})
const createPostUserFailureAction = (
  payload: PostUserFailurePayload,
): PostUserFailureAction => ({
  type: POST_USER_FAILURE,
  payload: payload,
})

export const postUserAndRedirectToSignin = (
  body: Partial<PostUserRequest>,
  acceptOrgInvite?: boolean,
): ThunkAction<
  Promise<PostUserActions>,
  RootState,
  void,
  PostUserActions | UpdateAcceptOrgInviteAction
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      PostUserActions | UpdateAcceptOrgInviteAction
    >,
    getState: () => RootState,
  ): Promise<PostUserActions> => {
    dispatch(createPostUserInitAction())
    return axios
      .post(`/create`, body, selectUnauthedJsonApiRequestConfig(getState()))
      .then(
        async (response) => {
          const createPostUserSuccess = dispatch(
            createPostUserSuccessAction(response.data),
          )
          dispatch(createUpdateAcceptOrgInviteAction(!!acceptOrgInvite))
          return createPostUserSuccess
        },
        (err) => {
          // Handle Cloudflare 403 for compromised passwords
          // The network request will not always directly send a 403 code back
          // but instead will show a network request error with no response object
          // If the user is considered online, but this request sends an ERR_NETWORK string back,
          // It's most likely Cloudflare sending back the 403 to block a compromised password.
          // Show the password compromise error message when the user is "online" AND the response
          // from the backend is ERR_NETWORK
          if (
            err?.response?.status === 403 ||
            (err?.code === 'ERR_NETWORK' &&
              // NOTE: window.navigator.onLine is unfortunately not 100% reliable, but it's our best option
              window?.navigator?.onLine &&
              !err.response)
          ) {
            return dispatch(
              createPostUserFailureAction({
                error: 'COMPROMISED_PASSWORD',
              }),
            )
          }
          return dispatch(
            createPostUserFailureAction(
              err.response?.data || {
                error: 'GENERIC_ERROR',
              },
            ),
          )
        },
      )
  }
}

export const POST_AUTH_FLOW_TYPE_INIT = 'auth/POST_AUTH_FLOW_TYPE_INIT'
export const POST_AUTH_FLOW_TYPE_SUCCESS = 'auth/POST_AUTH_FLOW_TYPE_SUCCESS'
export const POST_AUTH_FLOW_TYPE_FAILURE = 'auth/POST_AUTH_FLOW_TYPE_FAILURE'
export const CLEAR_AUTH_FLOW_TYPE = 'auth/CLEAR_AUTH_FLOW_TYPE'

interface PostAuthFlowTypeSuccessPayload {
  type: string
}

type PostAuthFlowTypeFailurePayload = BackendErrorPayload<string>

export interface PostAuthFlowTypeInitAction {
  type: typeof POST_AUTH_FLOW_TYPE_INIT
}
export interface PostAuthFlowTypeSuccessAction {
  type: typeof POST_AUTH_FLOW_TYPE_SUCCESS
  payload: PostAuthFlowTypeSuccessPayload
}
export interface PostAuthFlowTypeFailureAction {
  type: typeof POST_AUTH_FLOW_TYPE_FAILURE
  payload: PostAuthFlowTypeFailurePayload
}
export interface ClearAuthFlowTypeAction {
  type: typeof CLEAR_AUTH_FLOW_TYPE
}

export type PostAuthFlowTypeActions =
  | PostAuthFlowTypeInitAction
  | PostAuthFlowTypeSuccessAction
  | PostAuthFlowTypeFailureAction
  | ClearAuthFlowTypeAction

const createPostAuthFlowTypeInitAction = (): PostAuthFlowTypeInitAction => ({
  type: POST_AUTH_FLOW_TYPE_INIT,
})
const createPostAuthFlowTypeSuccessAction = (
  payload: PostAuthFlowTypeSuccessPayload,
): PostAuthFlowTypeSuccessAction => ({
  type: POST_AUTH_FLOW_TYPE_SUCCESS,
  payload,
})
const createPostAuthFlowTypeFailureAction = (
  payload: PostAuthFlowTypeFailurePayload,
): PostAuthFlowTypeFailureAction => ({
  type: POST_AUTH_FLOW_TYPE_FAILURE,
  payload: payload,
})
export const createClearAuthFlowTypeAction = (): ClearAuthFlowTypeAction => ({
  type: CLEAR_AUTH_FLOW_TYPE,
})

export const postAuthType = (
  body: AuthTypeRequest,
  initPasswordChange?: boolean,
): ThunkAction<Promise<void>, RootState, void, PostAuthFlowTypeActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostAuthFlowTypeActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostAuthFlowTypeInitAction())
    return axios
      .post(`/authtype`, body, selectUnauthedJsonApiRequestConfig(getState()))
      .then(
        async (response) => {
          dispatch(createPostAuthFlowTypeSuccessAction(response.data))
          // separate this out?
          if (initPasswordChange) {
            if (response.data.type === AuthTypes.auth0) {
              dispatch(postResetPassword(body.data))
            } else if (response.data.type === AuthTypes.cognito) {
              dispatch(
                postCognitoResetPasswordInit({
                  email: body.data,
                }),
              )
            }
          }
        },
        (err) => {
          const errorData = err.response?.data || {
            error: 'Network error occurred. Please check your connection.',
          }
          dispatch(createPostAuthFlowTypeFailureAction(errorData))
        },
      )
  }
}

export const POST_COGNITO_RESET_PASSWORD_INIT_INIT =
  'auth/POST_COGNITO_RESET_PASSWORD_INIT_INIT'
export const POST_COGNITO_RESET_PASSWORD_INIT_SUCCESS =
  'auth/POST_COGNITO_RESET_PASSWORD_INIT_SUCCESS'
export const POST_COGNITO_RESET_PASSWORD_INIT_FAILURE =
  'auth/POST_COGNITO_RESET_PASSWORD_INIT_FAILURE'
export const RESET_PASSWORD_RESET_STATE = 'auth/RESET_PASSWORD_RESET_STATE'

interface PostCognitoResetPasswordInitSuccessPayload {
  type: string
}

type PostCognitoResetPasswordInitFailurePayload = BackendErrorPayload<string>

export interface PostCognitoResetPasswordInitInitAction {
  type: typeof POST_COGNITO_RESET_PASSWORD_INIT_INIT
}
export interface PostCognitoResetPasswordInitSuccessAction {
  type: typeof POST_COGNITO_RESET_PASSWORD_INIT_SUCCESS
  payload: PostCognitoResetPasswordInitSuccessPayload
}
export interface PostCognitoResetPasswordInitFailureAction {
  type: typeof POST_COGNITO_RESET_PASSWORD_INIT_FAILURE
  payload: PostCognitoResetPasswordInitFailurePayload
}
export interface ResetPasswordResetStateAction {
  type: typeof RESET_PASSWORD_RESET_STATE
}

export type PostCognitoResetPasswordInitActions =
  | PostCognitoResetPasswordInitInitAction
  | PostCognitoResetPasswordInitSuccessAction
  | PostCognitoResetPasswordInitFailureAction
  | ResetPasswordResetStateAction

const createPostCognitoResetPasswordInitInitAction =
  (): PostCognitoResetPasswordInitInitAction => ({
    type: POST_COGNITO_RESET_PASSWORD_INIT_INIT,
  })
const createPostCognitoResetPasswordInitSuccessAction = (
  payload: PostCognitoResetPasswordInitSuccessPayload,
): PostCognitoResetPasswordInitSuccessAction => ({
  type: POST_COGNITO_RESET_PASSWORD_INIT_SUCCESS,
  payload,
})
const createPostCognitoResetPasswordInitFailureAction = (
  payload: PostCognitoResetPasswordInitFailurePayload,
): PostCognitoResetPasswordInitFailureAction => ({
  type: POST_COGNITO_RESET_PASSWORD_INIT_FAILURE,
  payload: payload,
})
export const createResetPasswordResetStateAction =
  (): ResetPasswordResetStateAction => ({
    type: RESET_PASSWORD_RESET_STATE,
  })

export const postCognitoResetPasswordInit = (
  body: Partial<CognitoResetPasswordInitRequest>,
  newCode?: boolean,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  PostCognitoResetPasswordInitActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      PostCognitoResetPasswordInitActions
    >,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostCognitoResetPasswordInitInitAction())
    return axios
      .post(
        `/reset-password/init`,
        body,
        selectUnauthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          dispatch(
            createPostCognitoResetPasswordInitSuccessAction(response.data),
          )
          if (newCode) {
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: <Trns source={'New code sent!'} />,
              }),
            )
          }
        },
        (err) => {
          dispatch(
            createPostCognitoResetPasswordInitFailureAction(err.response.data),
          )
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: <Trns source={'Code could not be sent'} />,
              failureColorScheme: true,
            }),
          )
        },
      )
  }
}

export const POST_COGNITO_RESET_PASSWORD_CONFIRM_INIT =
  'auth/POST_COGNITO_RESET_PASSWORD_CONFIRM_INIT'
export const POST_COGNITO_RESET_PASSWORD_CONFIRM_SUCCESS =
  'auth/POST_COGNITO_RESET_PASSWORD_CONFIRM_SUCCESS'
export const POST_COGNITO_RESET_PASSWORD_CONFIRM_FAILURE =
  'auth/POST_COGNITO_RESET_PASSWORD_CONFIRM_FAILURE'

interface PostCognitoResetPasswordConfirmSuccessPayload {
  type: string
}

type PostCognitoResetPasswordConfirmFailurePayload = BackendErrorPayload<string>

export interface PostCognitoResetPasswordConfirmInitAction {
  type: typeof POST_COGNITO_RESET_PASSWORD_CONFIRM_INIT
}
export interface PostCognitoResetPasswordConfirmSuccessAction {
  type: typeof POST_COGNITO_RESET_PASSWORD_CONFIRM_SUCCESS
  payload: PostCognitoResetPasswordConfirmSuccessPayload
}
export interface PostCognitoResetPasswordConfirmFailureAction {
  type: typeof POST_COGNITO_RESET_PASSWORD_CONFIRM_FAILURE
  payload: PostCognitoResetPasswordConfirmFailurePayload
}

export type PostCognitoResetPasswordConfirmActions =
  | PostCognitoResetPasswordConfirmInitAction
  | PostCognitoResetPasswordConfirmSuccessAction
  | PostCognitoResetPasswordConfirmFailureAction

const createPostCognitoResetPasswordConfirmInitAction =
  (): PostCognitoResetPasswordConfirmInitAction => ({
    type: POST_COGNITO_RESET_PASSWORD_CONFIRM_INIT,
  })
const createPostCognitoResetPasswordConfirmSuccessAction = (
  payload: PostCognitoResetPasswordConfirmSuccessPayload,
): PostCognitoResetPasswordConfirmSuccessAction => ({
  type: POST_COGNITO_RESET_PASSWORD_CONFIRM_SUCCESS,
  payload,
})
const createPostCognitoResetPasswordConfirmFailureAction = (
  payload: PostCognitoResetPasswordConfirmFailurePayload,
): PostCognitoResetPasswordConfirmFailureAction => ({
  type: POST_COGNITO_RESET_PASSWORD_CONFIRM_FAILURE,
  payload: payload,
})

export const postCognitoResetPasswordConfirm = (
  body: Partial<CognitoResetPasswordConfirmRequest>,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  PostCognitoResetPasswordConfirmActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      PostCognitoResetPasswordConfirmActions
    >,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostCognitoResetPasswordConfirmInitAction())
    return axios
      .post(
        `/reset-password/confirm`,
        body,
        selectUnauthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          dispatch(
            createPostCognitoResetPasswordConfirmSuccessAction(response.data),
          )
          dispatch(connectedPush(signinUrl()))
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <Trns source={'Success! Your password has been changed.'} />
              ),
            }),
          )
        },
        (err) => {
          dispatch(
            createPostCognitoResetPasswordConfirmFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const POST_MFA_PREFERENCE_INIT = 'auth/POST_MFA_PREFERENCE_INIT'
export const POST_MFA_PREFERENCE_SUCCESS = 'auth/POST_MFA_PREFERENCE_SUCCESS'
export const POST_MFA_PREFERENCE_FAILURE = 'auth/POST_MFA_PREFERENCE_FAILURE'

interface PostMfaPreferenceSuccessPayload {
  type: string
}

type PostMfaPreferenceFailurePayload = BackendErrorPayload<string>

export interface PostMfaPreferenceInitAction {
  type: typeof POST_MFA_PREFERENCE_INIT
}
export interface PostMfaPreferenceSuccessAction {
  type: typeof POST_MFA_PREFERENCE_SUCCESS
  payload: PostMfaPreferenceSuccessPayload
}
export interface PostMfaPreferenceFailureAction {
  type: typeof POST_MFA_PREFERENCE_FAILURE
  payload: PostMfaPreferenceFailurePayload
}

export type PostMfaPreferenceActions =
  | PostMfaPreferenceInitAction
  | PostMfaPreferenceSuccessAction
  | PostMfaPreferenceFailureAction

const createPostMfaPreferenceInitAction = (): PostMfaPreferenceInitAction => ({
  type: POST_MFA_PREFERENCE_INIT,
})

const createPostMfaPreferenceSuccessAction = (
  payload: PostMfaPreferenceSuccessPayload,
): PostMfaPreferenceSuccessAction => ({
  type: POST_MFA_PREFERENCE_SUCCESS,
  payload,
})
const createPostMfaPreferenceFailureAction = (
  payload: PostMfaPreferenceFailurePayload,
): PostMfaPreferenceFailureAction => ({
  type: POST_MFA_PREFERENCE_FAILURE,
  payload: payload,
})

export const postMfaPreference = (
  userName: string,
  preference?: MfaPreferences,
): ThunkAction<Promise<void>, RootState, void, PostMfaPreferenceActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostMfaPreferenceActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostMfaPreferenceInitAction())
    return axios
      .post(
        `/mfa`,
        {
          cognitoId: userName,
          preferred: preference || MfaPreferences.email,
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          dispatch(createPostMfaPreferenceSuccessAction(response.data))
          dispatch(getMe()) // reload current user
          dispatch(createSetIsToastOpen(false))
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <Trns source={"You've turned on two-factor authentication"} />
              ),
            }),
          )
        },
        (err) => {
          dispatch(createPostMfaPreferenceFailureAction(err.response.data))
        },
      )
  }
}

export const DELETE_MFA_PREFERENCE_INIT = 'auth/DELETE_MFA_PREFERENCE_INIT'
export const DELETE_MFA_PREFERENCE_SUCCESS =
  'auth/DELETE_MFA_PREFERENCE_SUCCESS'
export const DELETE_MFA_PREFERENCE_FAILURE =
  'auth/DELETE_MFA_PREFERENCE_FAILURE'

interface DeleteMfaPreferenceSuccessPayload {
  type: string
}

type DeleteMfaPreferenceFailurePayload = BackendErrorPayload<string>

export interface DeleteMfaPreferenceInitAction {
  type: typeof DELETE_MFA_PREFERENCE_INIT
}
export interface DeleteMfaPreferenceSuccessAction {
  type: typeof DELETE_MFA_PREFERENCE_SUCCESS
  payload: DeleteMfaPreferenceSuccessPayload
}
export interface DeleteMfaPreferenceFailureAction {
  type: typeof DELETE_MFA_PREFERENCE_FAILURE
  payload: DeleteMfaPreferenceFailurePayload
}

export type DeleteMfaPreferenceActions =
  | DeleteMfaPreferenceInitAction
  | DeleteMfaPreferenceSuccessAction
  | DeleteMfaPreferenceFailureAction

const createDeleteMfaPreferenceInitAction =
  (): DeleteMfaPreferenceInitAction => ({
    type: DELETE_MFA_PREFERENCE_INIT,
  })

const createDeleteMfaPreferenceSuccessAction = (
  payload: DeleteMfaPreferenceSuccessPayload,
): DeleteMfaPreferenceSuccessAction => ({
  type: DELETE_MFA_PREFERENCE_SUCCESS,
  payload,
})
const createDeleteMfaPreferenceFailureAction = (
  payload: DeleteMfaPreferenceFailurePayload,
): DeleteMfaPreferenceFailureAction => ({
  type: DELETE_MFA_PREFERENCE_FAILURE,
  payload: payload,
})

export const deleteMfaPreference = (): ThunkAction<
  Promise<void>,
  RootState,
  void,
  DeleteMfaPreferenceActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, DeleteMfaPreferenceActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createDeleteMfaPreferenceInitAction())
    return axios
      .delete(`/mfa`, selectAuthedJsonApiRequestConfig(getState()))
      .then(
        async (response) => {
          dispatch(createDeleteMfaPreferenceSuccessAction(response.data))
          dispatch(getMe()) // reload current user
          dispatch(createSetIsToastOpen(false))
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <Trns source={"You've turned off two-factor authentication"} />
              ),
            }),
          )
        },
        (err) => {
          dispatch(createDeleteMfaPreferenceFailureAction(err.response.data))
        },
      )
  }
}

export const UPDATE_ACCEPT_ORG_INVITE = 'auth/UPDATE_ACCEPT_ORG_INVITE'

export interface UpdateAcceptOrgInviteAction {
  type: typeof UPDATE_ACCEPT_ORG_INVITE
  payload: boolean
}

export const createUpdateAcceptOrgInviteAction = (
  payload: boolean,
): UpdateAcceptOrgInviteAction => ({
  type: UPDATE_ACCEPT_ORG_INVITE,
  payload,
})

export const UPDATE_ORG_INVITE_ERROR = 'auth/UPDATE_ORG_INVITE_ERROR'

export interface UpdateOrgInviteErrorAction {
  type: typeof UPDATE_ORG_INVITE_ERROR
  payload: boolean
}

export const createUpdateOrgInviteErrorAction = (
  payload: boolean,
): UpdateOrgInviteErrorAction => ({
  type: UPDATE_ORG_INVITE_ERROR,
  payload,
})

export const SET_UNAUTHED_LOCALE = 'auth/SET_UNAUTHED_LOCALE'

export interface SetUnauthedLocaleAction {
  type: typeof SET_UNAUTHED_LOCALE
  payload: ExtendLocales | undefined
}

export const createSetUnauthedLocaleAction = (
  payload: ExtendLocales | undefined,
): SetUnauthedLocaleAction => ({
  type: SET_UNAUTHED_LOCALE,
  payload,
})

export const POST_SEND_PHONE_VERIFICATION_CODE_INIT =
  'auth/POST_SEND_PHONE_VERIFICATION_CODE_INIT'
export const POST_SEND_PHONE_VERIFICATION_CODE_SUCCESS =
  'auth/POST_SEND_PHONE_VERIFICATION_CODE_SUCCESS'
export const POST_RESEND_PHONE_VERIFICATION_CODE_SUCCESS =
  'auth/POST_RESEND_PHONE_VERIFICATION_CODE_SUCCESS'
export const POST_SEND_PHONE_VERIFICATION_CODE_FAILURE =
  'auth/POST_SEND_PHONE_VERIFICATION_CODE_FAILURE'

export interface PostSendPhoneVerificationCodeSuccessPayload {
  msg: string
}

export interface PostSendPhoneVerificationCodeInitAction {
  type: typeof POST_SEND_PHONE_VERIFICATION_CODE_INIT
}

export interface PostSendPhoneVerificationCodeSuccessAction {
  type: typeof POST_SEND_PHONE_VERIFICATION_CODE_SUCCESS
  payload: PostSendPhoneVerificationCodeSuccessPayload
}

export interface PostResendPhoneVerificationCodeSuccessAction {
  type: typeof POST_RESEND_PHONE_VERIFICATION_CODE_SUCCESS
  payload: PostSendPhoneVerificationCodeSuccessPayload
}

export interface PostSendPhoneVerificationCodeFailureAction {
  type: typeof POST_SEND_PHONE_VERIFICATION_CODE_FAILURE
  payload: BackendErrorPayload<string>
}

export type PostSendPhoneVerificationCodeActions =
  | PostSendPhoneVerificationCodeInitAction
  | PostSendPhoneVerificationCodeSuccessAction
  | PostResendPhoneVerificationCodeSuccessAction
  | PostSendPhoneVerificationCodeFailureAction

export const createPostSendPhoneVerificationCodeInitAction =
  (): PostSendPhoneVerificationCodeInitAction => ({
    type: POST_SEND_PHONE_VERIFICATION_CODE_INIT,
  })

export const createPostSendPhoneVerificationCodeSuccessAction = (
  payload: PostSendPhoneVerificationCodeSuccessPayload,
): PostSendPhoneVerificationCodeSuccessAction => ({
  type: POST_SEND_PHONE_VERIFICATION_CODE_SUCCESS,
  payload: payload,
})

export const createPostResendPhoneVerificationCodeSuccessAction = (
  payload: PostSendPhoneVerificationCodeSuccessPayload,
): PostResendPhoneVerificationCodeSuccessAction => ({
  type: POST_RESEND_PHONE_VERIFICATION_CODE_SUCCESS,
  payload: payload,
})

export const createPostSendPhoneVerificationCodeFailureAction = (
  payload: BackendErrorPayload<string>,
): PostSendPhoneVerificationCodeFailureAction => ({
  type: POST_SEND_PHONE_VERIFICATION_CODE_FAILURE,
  payload: payload,
})

export const postSendPhoneVerificationCode = (
  email: string,
  resendVerification?: boolean,
): ThunkAction<
  Promise<PostSendPhoneVerificationCodeActions>,
  RootState,
  void,
  PostSendPhoneVerificationCodeActions | ResetVerificationErrorsAction
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      PostSendPhoneVerificationCodeActions | ResetVerificationErrorsAction
    >,
    getState: () => RootState,
  ): Promise<PostSendPhoneVerificationCodeActions> => {
    dispatch(createPostSendPhoneVerificationCodeInitAction())
    return axios
      .post(
        `/users/phone/sendverification`,
        { email },
        selectUnauthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          if (resendVerification) {
            dispatch(resetVerificationErrors())
            return dispatch(
              createPostResendPhoneVerificationCodeSuccessAction(response.data),
            )
          } else {
            dispatch(resetVerificationErrors())
            return dispatch(
              createPostSendPhoneVerificationCodeSuccessAction(response.data),
            )
          }
        },
        (err) => {
          return dispatch(
            createPostSendPhoneVerificationCodeFailureAction(err.response.data),
          )
        },
      )
  }
}

export const postAuthSendPhoneVerificationCode = (
  email: string,
  phone?: string,
  resendVerification?: boolean,
): ThunkAction<
  Promise<PostSendPhoneVerificationCodeActions>,
  RootState,
  void,
  PostSendPhoneVerificationCodeActions | ResetVerificationErrorsAction
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      PostSendPhoneVerificationCodeActions | ResetVerificationErrorsAction
    >,
    getState: () => RootState,
  ): Promise<PostSendPhoneVerificationCodeActions> => {
    dispatch(createPostSendPhoneVerificationCodeInitAction())
    return axios
      .post(
        `/users/phone/senduserverification`,
        { email, phone },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          if (resendVerification) {
            dispatch(resetVerificationErrors())
            return dispatch(
              createPostResendPhoneVerificationCodeSuccessAction(response.data),
            )
          } else {
            dispatch(resetVerificationErrors())
            return dispatch(
              createPostSendPhoneVerificationCodeSuccessAction(response.data),
            )
          }
        },
        (err) => {
          return dispatch(
            createPostSendPhoneVerificationCodeFailureAction(err.response.data),
          )
        },
      )
  }
}

export const POST_PHONE_VERIFICATION_INIT = 'auth/POST_PHONE_VERIFICATION_INIT'
export const POST_PHONE_VERIFICATION_SUCCESS =
  'auth/POST_PHONE_VERIFICATION_SUCCESS'
export const POST_PHONE_VERIFICATION_FAILURE =
  'auth/POST_PHONE_VERIFICATION_FAILURE'

export interface PostPhoneVerificationSuccessPayload {}

export interface PostPhoneVerificationInitAction {
  type: typeof POST_PHONE_VERIFICATION_INIT
}

export interface PostPhoneVerificationSuccessAction {
  type: typeof POST_PHONE_VERIFICATION_SUCCESS
  payload: PostPhoneVerificationSuccessPayload
}

export interface PostPhoneVerificationFailureAction {
  type: typeof POST_PHONE_VERIFICATION_FAILURE
  payload: BackendErrorPayload<string>
}

export type PostPhoneVerificationActions =
  | PostPhoneVerificationInitAction
  | PostPhoneVerificationSuccessAction
  | PostPhoneVerificationFailureAction

export const createPostPhoneVerificationInitAction =
  (): PostPhoneVerificationInitAction => ({
    type: POST_PHONE_VERIFICATION_INIT,
  })

export const createPostPhoneVerificationSuccessAction = (
  payload: PostPhoneVerificationSuccessPayload,
): PostPhoneVerificationSuccessAction => ({
  type: POST_PHONE_VERIFICATION_SUCCESS,
  payload,
})

export const createPostPhoneVerificationFailureAction = (
  payload: BackendErrorPayload<string>,
): PostPhoneVerificationFailureAction => ({
  type: POST_PHONE_VERIFICATION_FAILURE,
  payload: payload,
})

export const postAuthPhoneVerification = (
  email: string,
  code: string,
  openVerifyToast = false,
  phone?: string,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  PostPhoneVerificationActions | ResetVerificationErrorsAction
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      PostPhoneVerificationActions | ResetVerificationErrorsAction
    >,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostPhoneVerificationInitAction())
    return axios
      .post(
        `/users/phone/userverify`,
        { email, code, phone },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPostPhoneVerificationSuccessAction(response.data))
          dispatch(createSetIsUpdatePhoneNumberPopupOpen(false))
          dispatch(createSetIsVerifyPhoneNumberPopupOpen(false))
          dispatch(getMe())
          dispatch(resetVerificationErrors())
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: openVerifyToast ? (
                <VerifiedPhoneNumberToastMessage />
              ) : (
                <VerifiedUpdatedPhoneNumberToastMessage />
              ),
            }),
          )
        },
        (err) => {
          dispatch(createPostPhoneVerificationFailureAction(err.response.data))
        },
      )
  }
}

export const postPhoneVerificationAndSignIn = (
  email: string,
  code: string,
  password: string,
  acceptOrgInvite?: boolean,
  suppressRedirect?: boolean,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  PostPhoneVerificationActions | UpdateAcceptOrgInviteAction
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      PostPhoneVerificationActions | UpdateAcceptOrgInviteAction
    >,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostPhoneVerificationInitAction())
    return axios
      .post(
        `/users/phone/verify`,
        { email, code },
        selectUnauthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          const state = getState()
          const authType = selectAuthTypeFromState(state)
          const env = selectEnvironmentFromState(state)
          const axiosConfig = selectUnauthedJsonApiRequestConfig(state)
          const featureFlagger = selectFeatureFlaggerFromState(state)
          const ssoEnabled = isSSOLoginEnabled(featureFlagger)

          dispatch(createSetIsExtraVerificationPopupOpen(false))
          dispatch(
            connectedPush({
              pathname: signinUrl(),
              state: {
                password: password,
                acceptOrgInvite: !!acceptOrgInvite,
              },
              search: '',
              hash: '',
            }),
          )
          dispatch(createUpdateAcceptOrgInviteAction(!!acceptOrgInvite))
          if (password) {
            // Reconfigure Amplify with the correct user pool before signing in
            // This ensures we're talking to the pool where the user was created
            try {
              await configureAmplify(env, {
                email,
                axiosConfig,
                ssoEnabled,
              })
            } catch {
              // If authconfig fails, continue with existing config
              // The sign-in may still work if the user is in the expected pool
            }

            if (authType === AuthTypes.cognito) {
              await dispatch(
                postAmplifySignin(
                  email,
                  password,
                  false,
                  false,
                  acceptOrgInvite,
                ),
              )
            } else {
              await dispatch(
                postSigninThenPush(email, password, acceptOrgInvite),
              )
            }
          }
          startTimeStamp = moment().utc()
          if (!suppressRedirect) {
            if (acceptOrgInvite) {
              dispatch(connectedPush(homeUrl()))
            } else {
              dispatch(connectedPush(onboardingUrl()))
            }
          }
          dispatch(createPostPhoneVerificationSuccessAction(response.data))
          dispatch(createSetIsUpdatePhoneNumberPopupOpen(false))
        },
        (err) => {
          dispatch(createPostPhoneVerificationFailureAction(err.response.data))
        },
      )
  }
}

export const postUserWithoutPhoneVerificationAndSignIn = (
  email: string,
  password: string,
  acceptOrgInvite?: boolean,
  suppressRedirect?: boolean,
): ThunkAction<Promise<void>, RootState, void, UpdateAcceptOrgInviteAction> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, UpdateAcceptOrgInviteAction>,
    getState: () => RootState,
  ): Promise<void> => {
    const state = getState()
    const authType = selectAuthTypeFromState(state)
    const env = selectEnvironmentFromState(state)
    const axiosConfig = selectUnauthedJsonApiRequestConfig(state)
    const featureFlagger = selectFeatureFlaggerFromState(state)
    const ssoEnabled = isSSOLoginEnabled(featureFlagger)

    dispatch(
      connectedPush({
        pathname: signinUrl(),
        state: {
          password: password,
          acceptOrgInvite: !!acceptOrgInvite,
        },
        search: '',
        hash: '',
      }),
    )
    dispatch(createUpdateAcceptOrgInviteAction(!!acceptOrgInvite))
    if (password) {
      // Reconfigure Amplify with the correct user pool before signing in
      // This ensures we're talking to the pool where the user was created
      try {
        await configureAmplify(env, {
          email,
          axiosConfig,
          ssoEnabled,
        })
      } catch {
        // If authconfig fails, continue with existing config
        // The sign-in may still work if the user is in the expected pool
      }

      if (authType === AuthTypes.cognito) {
        await dispatch(
          postAmplifySignin(email, password, false, false, acceptOrgInvite),
        )
      } else {
        await dispatch(postSigninThenPush(email, password, acceptOrgInvite))
      }
    }
    startTimeStamp = moment().utc()
    if (!suppressRedirect) {
      if (acceptOrgInvite) {
        dispatch(connectedPush(homeUrl()))
      } else {
        dispatch(connectedPush(onboardingUrl()))
      }
    }
  }
}
